import {
  collection,
  doc,
  getDocs,
  getDocsFromServer,
  getDoc,
  getDocFromServer,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  increment,
  onSnapshot,
  arrayUnion,
} from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { app, auth } from './firebase';
import { db } from './firestoreDb';

const functions = getFunctions(app, 'us-central1');
import { Property, PropertyInquiry, UserProfile, PropertyDocumentItem, LocalizedText, ListingVerificationStatus, ModerationReport } from '../types';
import { generatePublicId, generatePropertySlug, generateSmartPropertySlug, getLocalizedPropertySlug } from './routes';
import { toSafeIsoString, getTimestampMs } from './dateUtils';
import { LISTING_CONFIG, calculateExpiryDate, isListingExpired } from '../config/listingConfig';
import { deriveCityAndRegionKeys, adminSetVerificationStatusBackend } from './listingLifecycleService';
import {
  isTestSandboxSession,
  saveSandboxProperty,
  getSandboxPropertyById,
  getSandboxPropertyBySlug,
} from './testSandboxService';
import { isDesignatedAdminEmail } from './adminAuthService';
import { sanitizeSafeString, sanitizeAntiInjection, checkRateLimit, evaluateInquiryRisk } from './securityService';
import { maskPhoneNumber, maskEmail } from './contactMasking';
import { fetchListingContact } from './contactService';
import { isDemoProperty } from './demoPropertyHelper';
import { translateListingContent, detectLanguage } from './translationService';
import { webmasterClient } from './webmasterClient';

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

/**
 * Recursively cleans and sanitizes data before sending to Cloud Firestore:
 * 1. Removes undefined values (strictly forbidden by Firestore).
 * 2. Neutralizes NoSQL operator injection keys (e.g. $gt, $where, prototype pollution).
 * 3. Sanitizes all strings against SQL injection, XSS, and dangerous payloads.
 * 4. Preserves Firestore FieldValues (serverTimestamp, increment, arrayUnion).
 */
export function cleanFirestoreData<T extends Record<string, any>>(data: T): Record<string, any> {
  const result: Record<string, any> = {};
  for (const key of Object.keys(data)) {
    // Defense against NoSQL operator injection and prototype pollution
    if (key.startsWith('$') || key === '__proto__' || key === 'constructor' || key === 'prototype') {
      continue;
    }
    const value = data[key];
    if (value !== undefined) {
      if (key === 'boundary_coordinates' && Array.isArray(value)) {
        // Firestore strictly forbids nested arrays (e.g. [[lat, lng]]).
        // Convert to array of coordinate objects [{ lat, lng }] which Firestore natively supports.
        result[key] = value.map((pt: any) => {
          if (Array.isArray(pt)) {
            return { lat: Number(pt[0]), lng: Number(pt[1]) };
          }
          return pt;
        });
      } else if (
        value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        !(value instanceof Date) &&
        !('_methodName' in value) &&
        !('_delegate' in value)
      ) {
        result[key] = cleanFirestoreData(value);
      } else if (typeof value === 'string') {
        result[key] = sanitizeAntiInjection(value, 15000);
      } else {
        result[key] = value;
      }
    }
  }
  return result;
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  };
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null): never {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map((provider) => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || [],
    },
    operationType,
    path,
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

/**
 * Validate connection to Cloud Firestore on boot
 */
export async function testConnection(): Promise<boolean> {
  try {
    await getDocFromServer(doc(db, 'test', 'connection'));
    return true;
  } catch (error) {
    if (error instanceof Error && error.message.includes('the client is offline')) {
      console.error('Please check your Firebase configuration.');
    }
    return false;
  }
}

export interface FirebaseProperty {
  id?: string;
  owner_id: string;
  owner_email?: string;
  owner_name?: string;
  owner_phone?: string;
  seller_type?: 'individual' | 'agent' | 'agency' | 'notary' | 'developer';
  contact_preference?: 'call_and_whatsapp' | 'whatsapp_only' | 'call_only';
  agency_registration_number?: string;
  developer_project_name?: string;
  notary_jurisdiction?: string;
  title: string;
  description: string;
  property_type: string;
  listing_type: 'sale' | 'rent' | 'short_stay';
  transaction_type?: 'sale' | 'rent' | 'stay';
  category_slug?: string;
  category_key?: string;
  category_attributes?: Record<string, any>;
  price: number;
  currency: string;
  country?: string;
  region: string;
  division?: string;
  subdivision?: string;
  city: string;
  neighborhood: string;
  location?: string;
  landmark?: string;
  latitude?: number;
  longitude?: number;
  bedrooms?: number;
  bathrooms?: number;
  area?: number;
  garages?: number;
  is_furnished?: boolean;
  has_swimming_pool?: boolean;
  has_wifi?: boolean;
  titre_foncier_number?: string;
  cadastral_lot_number?: string;
  land_tenure_type?: string;
  boundary_coordinates?: [number, number][];
  cadastral_beacons?: Array<{ id: string; label: string; lat: number; lng: number }>;
  calculated_area_sqm?: number;
  calculated_perimeter_meters?: number;
  status: 'draft' | 'pending_review' | 'active' | 'under_contract' | 'sold' | 'rented' | 'expired' | 'archived';
  published_at?: any;
  publishedAt?: any;
  expires_at?: any;
  expiresAt?: any;
  renewed_at?: any;
  renewedAt?: any;
  city_key?: string;
  cityKey?: string;
  region_key?: string;
  regionKey?: string;
  verification_status: 'unverified' | 'documents_uploaded' | 'cadastre_cross_checked' | 'verified' | 'disputed';
  verified?: boolean;
  verification_details?: {
    titre_foncier_number?: string;
    conservation_fonciere?: string;
    cadastral_volume?: string;
    cadastral_folio?: string;
    parcel_number?: string;
    surface_area_approved?: string;
    surveyor_name?: string;
    surveyor_ogec_number?: string;
    notary_assigned?: string;
    verified_at?: string;
    verified_by?: string;
    admin_notes?: string;
    rejection_reason?: string;
  };
  featured?: boolean;
  boost_plan?: 'standard' | 'vip' | 'developer';
  boost_expires_at?: string;
  boosted_at?: string;
  boost_transaction_id?: string;
  boost_operator?: 'mtn_momo' | 'orange_money';
  view_count?: number;
  leads_generated?: number;
  whatsapp_clicks?: number;
  call_clicks?: number;
  image?: string;
  images?: string[];
  public_id?: string;
  slug?: string;
  slug_en?: string;
  slug_fr?: string;
  slugEn?: string;
  slugFr?: string;
  canonical_url?: string;
  canonical_url_fr?: string;
  previous_slugs?: string[];
  localized_title?: LocalizedText;
  localized_description?: LocalizedText;
  original_language?: 'en' | 'fr';
  original_lang?: 'en' | 'fr';
  originalLanguage?: 'en' | 'fr';
  title_en?: string;
  title_fr?: string;
  description_en?: string;
  description_fr?: string;
  translation_hash?: string;
  translation_source?: 'auto' | 'manual';
  translation_status?: 'completed' | 'fallback' | 'failed' | 'human_edited';
  translated_at?: any;
  deleted_at?: any;
  created_at?: any;
  updated_at?: any;
}

export interface FirebaseInquiry {
  id?: string;
  property_id: string;
  property_title?: string;
  property_location?: string;
  property_price?: string;
  property_type?: string;
  owner_id?: string;
  owner_name?: string;
  owner_email?: string;
  owner_phone?: string;
  sender_user_id?: string;
  sender_name: string;
  sender_phone: string;
  sender_email?: string;
  inquiry_type: string;
  message: string;
  preferred_visit_date?: string;
  offered_price?: number;
  status?: string;
  created_at?: any;
}

export interface FirebaseFavorite {
  id?: string;
  user_id: string;
  property_id: string;
  created_at?: any;
}

export { isDemoProperty } from './demoPropertyHelper';

/**
 * Normalizes property document data loaded from Cloud Firestore.
 * Converts coordinate objects [{ lat, lng }] back into Leaflet polygon tuples [lat, lng].
 * Derives boundary_coordinates from cadastral_beacons if missing.
 */
export function normalizePropertyData(id: string, raw: any): FirebaseProperty {
  const data = { ...raw, id };

  if (data.boundary_coordinates && Array.isArray(data.boundary_coordinates)) {
    data.boundary_coordinates = data.boundary_coordinates.map((pt: any) => {
      if (Array.isArray(pt)) return pt;
      if (pt && typeof pt === 'object' && ('lat' in pt || 'latitude' in pt)) {
        return [Number(pt.lat ?? pt.latitude), Number(pt.lng ?? pt.longitude)];
      }
      return pt;
    });
  } else if (data.cadastral_beacons && Array.isArray(data.cadastral_beacons) && data.cadastral_beacons.length >= 3) {
    data.boundary_coordinates = data.cadastral_beacons.map((b: any) => [
      Number(b.lat ?? b.latitude),
      Number(b.lng ?? b.longitude),
    ]);
  }

  // Ensure every listing has a canonical public_id, slug, and canonical_url
  if (!data.public_id) {
    data.public_id = generatePublicId();
  }
  if (!data.slug) {
    data.slug = generatePropertySlug({
      propertyType: data.property_type,
      listingType: data.listing_type,
      city: data.city,
      publicId: data.public_id,
      id,
    });
  }
  if (!data.slug_en && data.slug) {
    data.slug_en = getLocalizedPropertySlug(data.slug, 'en');
  }
  if (!data.slug_fr && data.slug) {
    data.slug_fr = getLocalizedPropertySlug(data.slug, 'fr');
  }
  if (!data.canonical_url) {
    data.canonical_url = `https://cameroonlandmarket.com/en/property/${data.slug_en || data.slug}`;
  }
  if (!data.canonical_url_fr) {
    data.canonical_url_fr = `https://cameroonlandmarket.com/fr/propriete/${data.slug_fr || data.slug}`;
  }
  if (!data.previous_slugs) {
    data.previous_slugs = [];
  }

  // Ensure contact details are always masked for public browsing
  if (data.owner_phone) {
    data.owner_phone = maskPhoneNumber(data.owner_phone);
  }
  if (data.owner_email) {
    data.owner_email = maskEmail(data.owner_email);
  }

  return data as FirebaseProperty;
}

/**
 * Real-time listener for properties in Cloud Firestore.
 * Automatically pushes live updates whenever any property is created, updated, or deleted.
 */
export function subscribeToProperties(
  callback: (properties: FirebaseProperty[]) => void,
  maxLimit = 150
): () => void {
  try {
    const colRef = collection(db, 'properties');
    const boundedQuery = query(colRef, limit(maxLimit));
    return onSnapshot(
      boundedQuery,
      (snapshot) => {
        const list = snapshot.docs
          .map((docSnap) => normalizePropertyData(docSnap.id, docSnap.data()))
          .filter((p: any) => !isDemoProperty(p))
          .sort((a: any, b: any) => {
            return getTimestampMs(b.created_at || b.createdAt) - getTimestampMs(a.created_at || a.createdAt);
          }) as FirebaseProperty[];
        callback(list);
      },
      (error) => {
        console.warn('Real-time properties listener notice (falling back to server queries):', error);
      }
    );
  } catch (err) {
    console.warn('Failed to attach real-time properties listener:', err);
    return () => {};
  }
}

/**
 * Fetch properties from Cloud Firestore with quota protection limit
 */
export async function getAllPropertiesFromFirestore(maxLimit = 150): Promise<FirebaseProperty[]> {
  try {
    let snap;
    const q = query(collection(db, 'properties'), limit(maxLimit));
    try {
      snap = await getDocs(q);
    } catch {
      snap = await getDocsFromServer(q);
    }

    return snap.docs
      .map((docSnap) => normalizePropertyData(docSnap.id, docSnap.data()))
      .filter((p: any) => !isDemoProperty(p)) as FirebaseProperty[];
  } catch (error) {
    console.error('Error getting all properties from Firestore:', error);
    return [];
  }
}

/**
 * Fetch active properties from Cloud Firestore with quota protection limit
 */
export async function getActiveProperties(maxLimit = 150): Promise<FirebaseProperty[]> {
  try {
    let snap;
    const q = query(collection(db, 'properties'), limit(maxLimit));
    try {
      snap = await getDocs(q);
    } catch {
      snap = await getDocsFromServer(q);
    }

    const all = snap.docs.map((docSnap) => normalizePropertyData(docSnap.id, docSnap.data())) as FirebaseProperty[];

    return all.filter((p: any) => {
      if (isDemoProperty(p)) return false;
      const status = (p.status || '').toLowerCase().trim();
      const isPub = p.isPublic !== undefined ? Boolean(p.isPublic) : (status === 'published' || status === 'active');
      if (!isPub) return false;
      if (status !== 'published' && status !== 'active') return false;
      const expiresAt = p.expires_at || p.expiresAt;
      if (expiresAt && isListingExpired(expiresAt)) return false;
      return true;
    });
  } catch (error) {
    console.error('Error getting properties from Firestore:', error);
    return [];
  }
}

/**
 * Fetch UserProfile from Firestore
 */
export async function getUserProfileFromFirestore(uid: string): Promise<UserProfile | null> {
  try {
    const docRef = doc(db, 'users', uid);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() } as UserProfile;
  } catch (error) {
    console.error('Error fetching user profile from Firestore:', error);
    return null;
  }
}

/**
 * Save complete UserProfile to Firestore
 */
export async function saveUserProfileToFirestore(profile: UserProfile): Promise<void> {
  try {
    const docRef = doc(db, 'users', profile.id);
    const cleaned = cleanFirestoreData({
      ...profile,
      updated_at: new Date().toISOString(),
    });
    await setDoc(docRef, cleaned, { merge: true });

    // Sync non-sensitive public directory fields to public_profiles
    try {
      const publicDocRef = doc(db, 'public_profiles', profile.id);
      await setDoc(publicDocRef, cleanFirestoreData({
        id: profile.id,
        fullName: profile.fullName,
        role: profile.role,
        avatar: profile.avatar || profile.avatarUrl || null,
        city: profile.city || null,
        region: profile.region || null,
        bio: profile.bio || null,
        companyName: profile.companyName || null,
        professionalSpecialty: profile.professionalSpecialty || null,
        isVerified: profile.isVerified || false,
        updated_at: new Date().toISOString(),
      }), { merge: true });
    } catch (pubErr) {
      console.warn('Notice syncing public profile:', pubErr);
    }
  } catch (error) {
    console.warn('Notice saving user profile to Cloud Firestore (local session maintained):', error);
  }
}

/**
 * Update partial UserProfile in Firestore
 */
export async function updateUserProfileInFirestore(uid: string, updates: Partial<UserProfile>): Promise<void> {
  const cleaned = cleanFirestoreData({
    ...updates,
    updated_at: new Date().toISOString(),
  });

  try {
    const docRef = doc(db, 'users', uid);
    await updateDoc(docRef, cleaned);
  } catch (error) {
    console.warn(`Notice updating user profile ${uid} in Firestore, attempting setDoc merge fallback:`, error);
    try {
      const docRef = doc(db, 'users', uid);
      await setDoc(docRef, cleaned, { merge: true });
    } catch (innerErr) {
      console.warn('Fallback setDoc profile notice:', innerErr);
    }
  }

  // Also sync public fields to public_profiles if modified
  const publicKeys: (keyof UserProfile)[] = ['fullName', 'role', 'avatar', 'city', 'region', 'bio', 'companyName', 'professionalSpecialty', 'isVerified'];
  const hasPublicFields = Object.keys(updates).some((k) => publicKeys.includes(k as keyof UserProfile));
  if (hasPublicFields) {
    try {
      const publicDocRef = doc(db, 'public_profiles', uid);
      const pubData: Record<string, any> = { updated_at: new Date().toISOString() };
      for (const k of publicKeys) {
        if (k in updates) {
          pubData[k] = (updates as any)[k] ?? null;
        }
      }
      await setDoc(publicDocRef, cleanFirestoreData(pubData), { merge: true });
    } catch (pubErr) {
      console.warn('Notice updating public profile:', pubErr);
    }
  }
}

/**
 * Fetch all users from Firestore (For Admin directory, or fallback to safe public_profiles for public)
 */
export async function getAllUsersFromFirestore(): Promise<UserProfile[]> {
  try {
    const snap = await getDocs(collection(db, 'users'));
    return snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    })) as UserProfile[];
  } catch (error) {
    // If access is restricted (e.g. public directory visitors), read from safe public_profiles collection
    try {
      const pubSnap = await getDocs(collection(db, 'public_profiles'));
      return pubSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as UserProfile[];
    } catch (pubErr) {
      console.warn('Could not fetch users or public profiles:', pubErr);
      return [];
    }
  }
}

/**
 * Permanently delete a user profile and credentials from Cloud Firestore
 */
export async function deleteUserFromFirestore(uid: string): Promise<boolean> {
  try {
    await deleteDoc(doc(db, 'users', uid)).catch(() => {});
    await deleteDoc(doc(db, 'public_profiles', uid)).catch(() => {});
    await deleteDoc(doc(db, 'admins', uid)).catch(() => {});
    return true;
  } catch (err) {
    console.warn(`Could not delete user ${uid} from Firestore:`, err);
    return false;
  }
}

/**
 * Securely record and verify administrator privilege document in Firestore
 */
export async function recordAdminVerificationInFirestore(uid: string, email: string): Promise<boolean> {
  try {
    const adminDocRef = doc(db, 'admins', uid);
    await setDoc(adminDocRef, {
      uid,
      email: email.toLowerCase().trim(),
      role: 'Admin',
      verified: true,
      updated_at: new Date().toISOString(),
    }, { merge: true });
    return true;
  } catch (err) {
    console.warn('Could not record admin verification in Firestore:', err);
    return false;
  }
}

/**
 * Securely verify administrator privilege against Firestore server database
 */
export async function verifyAdminRoleInFirestore(uid: string, email?: string): Promise<boolean> {
  try {
    if (email && isDesignatedAdminEmail(email)) {
      return true;
    }
    // Check in dedicated admins collection strictly
    const adminDocRef = doc(db, 'admins', uid);
    const adminSnap = await getDoc(adminDocRef);
    if (adminSnap.exists()) {
      const data = adminSnap.data();
      if (
        data?.role === 'Admin' ||
        data?.role === 'Super Admin' ||
        data?.role === 'Listing Approval Admin' ||
        isDesignatedAdminEmail(data?.email)
      ) {
        return true;
      }
    }
    return false;
  } catch (err) {
    console.warn('Could not verify admin role from Firestore:', err);
    return false;
  }
}

function convertSandboxToFirebaseProperty(p: Property): FirebaseProperty {
  return {
    id: p.id,
    owner_id: p.ownerId || 'sandbox-runner',
    owner_email: p.ownerEmail || 'e2e.test.runner@cameroonlandmarket.com',
    owner_name: p.ownerName || 'QA Automation Engineer',
    owner_phone: p.ownerPhone || '677998877',
    seller_type: (p.sellerType as any) || 'individual',
    contact_preference: (p.contactPreference as any) || 'call_and_whatsapp',
    title: p.title,
    description: p.description,
    property_type: p.type || 'land',
    listing_type: 'sale',
    price: p.priceRaw || 0,
    currency: 'XAF',
    region: p.region || 'Centre',
    city: p.city || 'Yaoundé',
    neighborhood: p.neighborhood || 'Bastos',
    location: p.location,
    status: 'active',
    public_id: p.publicId,
    slug: p.slug,
    canonical_url: p.canonicalUrl || `https://cameroonlandmarket.com/en/property/${p.slug}`,
    image: p.image,
    images: p.images,
    published_at: p.publishedAt,
    expires_at: p.expiresAt,
  } as FirebaseProperty;
}

/**
 * Fetch a single property by ID
 */
export async function getPropertyById(propertyId: string): Promise<FirebaseProperty | null> {
  const sandbox = getSandboxPropertyById(propertyId);
  if (sandbox) {
    return convertSandboxToFirebaseProperty(sandbox);
  }
  try {
    const docRef = doc(db, 'properties', propertyId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    return normalizePropertyData(snap.id, snap.data());
  } catch (error) {
    console.error('Error getting property by ID:', error);
    return null;
  }
}

/**
 * Fetch a single property by either its canonical slug, public ID (CLMxxxxx), or Firestore document ID.
 * Bypasses local state delays and ensures direct URL visitors load their property seamlessly.
 */
export async function getPropertyBySlugOrId(identifier: string): Promise<FirebaseProperty | null> {
  if (!identifier) return null;
  const cleanId = identifier.trim();

  // Check test sandbox store first
  const sandbox = getSandboxPropertyBySlug(cleanId) || getSandboxPropertyById(cleanId);
  if (sandbox) {
    return convertSandboxToFirebaseProperty(sandbox);
  }

  try {
    // 1. Direct document ID lookup
    const direct = await getPropertyById(cleanId);
    if (direct && !isDemoProperty(direct)) return direct;

    // 2. Query by slug, slug_en, slug_fr
    const slugQuery = query(
      collection(db, 'properties'),
      where('slug', '==', cleanId.toLowerCase()),
      limit(1)
    );
    let slugSnap = await getDocs(slugQuery);
    if (slugSnap.empty) {
      const slugEnQuery = query(
        collection(db, 'properties'),
        where('slug_en', '==', cleanId.toLowerCase()),
        limit(1)
      );
      slugSnap = await getDocs(slugEnQuery);
    }
    if (slugSnap.empty) {
      const slugFrQuery = query(
        collection(db, 'properties'),
        where('slug_fr', '==', cleanId.toLowerCase()),
        limit(1)
      );
      slugSnap = await getDocs(slugFrQuery);
    }
    if (!slugSnap.empty) {
      const p = normalizePropertyData(slugSnap.docs[0].id, slugSnap.docs[0].data());
      if (!isDemoProperty(p)) return p;
    }

    // 3. Query by public_id (case-insensitive)
    const pubQuery = query(
      collection(db, 'properties'),
      where('public_id', 'in', [cleanId.toLowerCase(), cleanId.toUpperCase()]),
      limit(1)
    );
    const pubSnap = await getDocs(pubQuery);
    if (!pubSnap.empty) {
      const p = normalizePropertyData(pubSnap.docs[0].id, pubSnap.docs[0].data());
      if (!isDemoProperty(p)) return p;
    }

    // 4. Extract trailing short ID suffix (e.g. "titled-land-600m2-buea-a1b2c3" -> "a1b2c3")
    const parts = cleanId.split('-');
    if (parts.length > 1) {
      const suffix = parts[parts.length - 1].trim();
      if (suffix && suffix.length >= 4) {
        // 4a. Check direct doc lookup by suffix
        const suffixDoc = await getPropertyById(suffix);
        if (suffixDoc && !isDemoProperty(suffixDoc)) return suffixDoc;

        // 4b. Query by public_id == suffix (case-insensitive)
        const pubSuffixQuery = query(
          collection(db, 'properties'),
          where('public_id', 'in', [suffix.toLowerCase(), suffix.toUpperCase()]),
          limit(1)
        );
        const pubSuffixSnap = await getDocs(pubSuffixQuery);
        if (!pubSuffixSnap.empty) {
          const p = normalizePropertyData(pubSuffixSnap.docs[0].id, pubSuffixSnap.docs[0].data());
          if (!isDemoProperty(p)) return p;
        }

        // 4c. Query by short_id == suffix
        const shortIdQuery = query(
          collection(db, 'properties'),
          where('short_id', '==', suffix.toLowerCase()),
          limit(1)
        );
        const shortIdSnap = await getDocs(shortIdQuery);
        if (!shortIdSnap.empty) {
          const p = normalizePropertyData(shortIdSnap.docs[0].id, shortIdSnap.docs[0].data());
          if (!isDemoProperty(p)) return p;
        }
      }
    }

    return null;
  } catch (error) {
    console.error('Error getting property by slug or ID:', error);
    return null;
  }
}

/**
 * Maps a normalized FirebaseProperty document to the application's Property type
 */
export function mapFirebasePropertyToAppProperty(rp: FirebaseProperty): Property {
  return {
    id: rp.id || `prop-${Date.now()}`,
    ownerId: rp.owner_id,
    ownerEmail: maskEmail(rp.owner_email || (rp as any).ownerEmail),
    ownerName: rp.owner_name,
    ownerPhone: maskPhoneNumber(rp.owner_phone || (rp as any).ownerPhone),
    sellerType: rp.seller_type || (rp as any).sellerType,
    contactPreference: rp.contact_preference || (rp as any).contactPreference || 'call_and_whatsapp',
    agencyRegistrationNumber: rp.agency_registration_number || (rp as any).agencyRegistrationNumber,
    developerProjectName: rp.developer_project_name || (rp as any).developerProjectName,
    notaryJurisdiction: rp.notary_jurisdiction || (rp as any).notaryJurisdiction,
    title: rp.title,
    description: rp.description,
    location: `${rp.city || 'Yaoundé'}, ${rp.neighborhood || ''}`,
    country: rp.country || 'Cameroon',
    region: rp.region,
    division: rp.division,
    city: rp.city,
    neighborhood: rp.neighborhood,
    subdivision: rp.subdivision,
    transactionType: (rp.transaction_type || (rp.listing_type === 'short_stay' ? 'stay' : rp.listing_type)) as any,
    categorySlug: rp.category_slug,
    categoryKey: rp.category_key || rp.category_slug,
    categoryAttributes: rp.category_attributes,
    price: `${rp.price?.toLocaleString()} ${rp.currency || 'XAF'}`,
    priceRaw: rp.price,
    type: (rp.property_type as any) || 'land',
    status: isListingExpired(rp.expires_at || (rp as any).expiresAt) && ((rp.status as any) === 'active' || (rp.status as any) === 'published' || !rp.status)
      ? 'expired'
      : ((rp.status as any) === 'active' ? 'published' : ((rp.status as any) || 'published')),
    isPublic: (rp as any).isPublic !== undefined
      ? Boolean((rp as any).isPublic)
      : ((rp.status as any) === 'published' || (rp.status as any) === 'active'),
    publishedAt: toSafeIsoString(rp.published_at || (rp as any).publishedAt) || null,
    expiresAt: toSafeIsoString(rp.expires_at || (rp as any).expiresAt) || null,
    renewedAt: toSafeIsoString(rp.renewed_at || (rp as any).renewedAt) || undefined,
    updatedAt: toSafeIsoString(rp.updated_at || (rp as any).updatedAt) || undefined,
    cityKey: rp.city_key || (rp as any).cityKey,
    regionKey: rp.region_key || (rp as any).regionKey,
    categoryBadge:
      rp.listing_type === 'rent'
        ? 'FOR RENT'
        : rp.listing_type === 'short_stay'
        ? 'HOTEL / STAY'
        : 'FOR SALE',
    badgeColor:
      rp.listing_type === 'rent'
        ? 'bg-blue-600'
        : rp.listing_type === 'short_stay'
        ? 'bg-cyan-600'
        : 'bg-emerald-600',
    image:
      rp.image ||
      (rp.images && rp.images[0]) ||
      '/images/default-property.jpg',
    images:
      rp.images && rp.images.length > 0
        ? rp.images
        : rp.image
        ? [rp.image]
        : undefined,
    specs: {
      area: rp.area ? `${rp.area} sqm` : ((rp as any).specs?.area || undefined),
      beds: rp.bedrooms || undefined,
      baths: rp.bathrooms || undefined,
    },
    coordinates:
      rp.latitude && rp.longitude ? [rp.latitude, rp.longitude] : undefined,
    boundaryCoordinates: (rp as any).boundary_coordinates
      ? (rp as any).boundary_coordinates.map((pt: any) =>
          Array.isArray(pt) ? pt : [Number(pt.lat ?? pt.latitude), Number(pt.lng ?? pt.longitude)]
        )
      : (rp as any).cadastral_beacons && (rp as any).cadastral_beacons.length >= 3
      ? (rp as any).cadastral_beacons.map((b: any) => [Number(b.lat ?? b.latitude), Number(b.lng ?? b.longitude)])
      : undefined,
    cadastralBeacons: (rp as any).cadastral_beacons,
    calculatedAreaSqm: (rp as any).calculated_area_sqm,
    calculatedPerimeterMeters: (rp as any).calculated_perimeter_meters,
    titreFoncier: rp.titre_foncier_number,
    verificationStatus: (rp as any).verificationStatus === 'verified' || (rp.verification_status as any) === 'verified' || (rp as any).verified === true
      ? 'verified'
      : (rp as any).verificationStatus === 'pending' || (rp.verification_status as any) === 'pending' || (rp.verification_status as any) === 'documents_uploaded'
      ? 'pending'
      : (rp as any).verificationStatus === 'rejected' || (rp.verification_status as any) === 'rejected'
      ? 'rejected'
      : 'unverified',
    verificationDetails: rp.verification_details ? {
      titreFoncierNumber: rp.verification_details.titre_foncier_number || (rp.verification_details as any).titreFoncierNumber,
      conservationFonciere: rp.verification_details.conservation_fonciere || (rp.verification_details as any).conservationFonciere,
      cadastralVolume: rp.verification_details.cadastral_volume || (rp.verification_details as any).cadastralVolume,
      cadastralFolio: rp.verification_details.cadastral_folio || (rp.verification_details as any).cadastralFolio,
      parcelNumber: rp.verification_details.parcel_number || (rp.verification_details as any).parcelNumber,
      surfaceAreaApproved: rp.verification_details.surface_area_approved || (rp.verification_details as any).surfaceAreaApproved,
      surveyorName: rp.verification_details.surveyor_name || (rp.verification_details as any).surveyorName,
      surveyorOgecNumber: rp.verification_details.surveyor_ogec_number || (rp.verification_details as any).surveyorOgecNumber,
      notaryAssigned: rp.verification_details.notary_assigned || (rp.verification_details as any).notaryAssigned,
      verifiedAt: rp.verification_details.verified_at || (rp.verification_details as any).verifiedAt,
      verifiedBy: rp.verification_details.verified_by || (rp.verification_details as any).verifiedBy,
      adminNotes: (auth.currentUser && (auth.currentUser.uid === rp.owner_id || isDesignatedAdminEmail(auth.currentUser.email)))
        ? (rp.verification_details.admin_notes || (rp.verification_details as any).adminNotes)
        : undefined,
      rejectionReason: (auth.currentUser && (auth.currentUser.uid === rp.owner_id || isDesignatedAdminEmail(auth.currentUser.email)))
        ? (rp.verification_details.rejection_reason || (rp.verification_details as any).rejectionReason)
        : undefined,
    } : (rp as any).verificationDetails,
    featured: rp.featured,
    boostPlan: (rp as any).boost_plan,
    boostExpiresAt: (rp as any).boost_expires_at,
    boostedAt: (rp as any).boosted_at,
    boostTransactionId: (rp as any).boost_transaction_id,
    boostOperator: (rp as any).boost_operator,
    views: rp.view_count || (rp as any).views || 0,
    leads_generated: rp.leads_generated || (rp as any).leads_generated || 0,
    whatsapp_clicks: rp.whatsapp_clicks || (rp as any).whatsapp_clicks || 0,
    call_clicks: rp.call_clicks || (rp as any).call_clicks || 0,
    createdAt: toSafeIsoString(
      rp.created_at || (rp as any).createdAt || rp.published_at || (rp as any).publishedAt,
      new Date().toISOString()
    ),
    publicId: rp.public_id || (rp as any).publicId,
    slug: rp.slug || (rp as any).slug,
    slugEn: rp.slug_en || (rp as any).slugEn || (rp.slug ? getLocalizedPropertySlug(rp.slug, 'en') : undefined),
    slugFr: rp.slug_fr || (rp as any).slugFr || (rp.slug ? getLocalizedPropertySlug(rp.slug, 'fr') : undefined),
    slug_en: rp.slug_en || (rp as any).slug_en || (rp.slug ? getLocalizedPropertySlug(rp.slug, 'en') : undefined),
    slug_fr: rp.slug_fr || (rp as any).slug_fr || (rp.slug ? getLocalizedPropertySlug(rp.slug, 'fr') : undefined),
    canonicalUrl: rp.canonical_url || (rp as any).canonicalUrl,
    previousSlugs: rp.previous_slugs || (rp as any).previousSlugs || [],
    localizedTitle: {
      en: rp.title_en || rp.localized_title?.en || rp.title,
      fr: rp.title_fr || rp.localized_title?.fr || rp.title,
      originalLanguage: rp.original_lang || (rp.original_language as any) || 'fr',
      ...(rp.localized_title || {}),
    } as any,
    localizedDescription: {
      en: rp.description_en || rp.localized_description?.en || rp.description,
      fr: rp.description_fr || rp.localized_description?.fr || rp.description,
      originalLanguage: rp.original_lang || (rp.original_language as any) || 'fr',
      ...(rp.localized_description || {}),
    } as any,
    originalLanguage: rp.original_language as any,
    original_lang: rp.original_lang || (rp.original_language as any),
    title_en: rp.title_en || rp.localized_title?.en,
    title_fr: rp.title_fr || rp.localized_title?.fr,
    description_en: rp.description_en || rp.localized_description?.en,
    description_fr: rp.description_fr || rp.localized_description?.fr,
    translation_hash: rp.translation_hash,
    translation_source: rp.translation_source,
    translation_status: rp.translation_status,
    translated_at: rp.translated_at,
  };
}

/**
 * Add or save a new property to Cloud Firestore.
 * Automatically generates a sanitized, collision-free canonical URL and permanent unique public ID.
 * Pattern: /property/{property-type}-{transaction-type}-{city}-{unique-id}
 */
export async function createProperty(property: Omit<FirebaseProperty, 'id'>, customId?: string): Promise<string> {
  // 0. Anti-fake / Anti-mock listing strict validation
  if (isDemoProperty({ id: customId, ...property })) {
    throw new Error('Creation of mock, demo, test, or non-authentic listings is strictly prohibited on Cameroon Land Market.');
  }

  // 1. Generate or validate permanent unique public ID (CLMxxxxx)
  let publicId = property.public_id?.trim();
  if (!publicId) {
    publicId = generatePublicId();
  }

  // 2. Generate canonical SEO slug: {property-type}-{transaction-type}-{city}-{unique-id}
  let slug = property.slug?.trim();
  if (!slug) {
    slug = generatePropertySlug({
      propertyType: property.property_type,
      listingType: property.listing_type,
      city: property.city,
      publicId,
    });
  }

  // 3. Database uniqueness check for slug & public ID
  try {
    const slugQuery = query(collection(db, 'properties'), where('slug', '==', slug), limit(1));
    const slugSnap = await getDocs(slugQuery);
    if (!slugSnap.empty) {
      // Collision detected! Regenerate unique publicId & slug
      publicId = generatePublicId();
      slug = generatePropertySlug({
        propertyType: property.property_type,
        listingType: property.listing_type,
        city: property.city,
        publicId,
      });
    }
  } catch (err) {
    // If offline or query fails, proceed with cryptographically random ID
  }

  const canonicalUrl = `https://cameroonlandmarket.com/en/property/${slug}`;

  // Derive canonical cityKey and regionKey
  const { cityKey, regionKey, cityName, regionName } = deriveCityAndRegionKeys({
    city: property.city,
    region: property.region,
    location: property.location,
    cityKey: property.city_key || property.cityKey,
    regionKey: property.region_key || property.regionKey,
  });

  const isPublicActive = !property.status || (property.status as string) === 'active' || (property.status as string) === 'published';
  const now = new Date();
  const nowIso = now.toISOString();
  const calculatedExpiry = calculateExpiryDate(now, LISTING_CONFIG.DEFAULT_EXPIRY_DAYS).toISOString();

  // 4. Automated Bilingual Translation Pipeline (EN & FR)
  let titleEn = property.title_en || (property as any).titleEn || '';
  let titleFr = property.title_fr || (property as any).titleFr || '';
  let descEn = property.description_en || (property as any).descriptionEn || '';
  let descFr = property.description_fr || (property as any).descriptionFr || '';
  const rawTitle = (property.title || titleEn || titleFr || '').trim();
  const rawDesc = (property.description || descEn || descFr || '').trim();
  const detectedLang: 'en' | 'fr' =
    property.original_lang ||
    property.original_language ||
    (property as any).originalLanguage ||
    detectLanguage(`${rawTitle} ${rawDesc}`);

  if (!titleEn || !titleFr || !descEn || !descFr) {
    try {
      const trans = await translateListingContent({
        title: rawTitle,
        description: rawDesc,
        originalLanguage: detectedLang,
      });
      titleEn = trans.localizedTitle.en;
      titleFr = trans.localizedTitle.fr;
      descEn = trans.localizedDescription.en;
      descFr = trans.localizedDescription.fr;
    } catch {}
  }

  const smartSlugEn = generateSmartPropertySlug({
    propertyType: property.property_type,
    listingType: property.listing_type,
    city: property.city,
    publicId,
    title: titleEn || rawTitle,
    titreFoncier: (property as any).titre_foncier || (property as any).titreFoncier,
    hasTitle: Boolean((property as any).has_title || (property as any).hasTitle),
    isFurnished: Boolean((property as any).is_furnished || (property as any).isFurnished),
  }, 'en');

  const smartSlugFr = generateSmartPropertySlug({
    propertyType: property.property_type,
    listingType: property.listing_type,
    city: property.city,
    publicId,
    title: titleFr || rawTitle,
    titreFoncier: (property as any).titre_foncier || (property as any).titreFoncier,
    hasTitle: Boolean((property as any).has_title || (property as any).hasTitle),
    isFurnished: Boolean((property as any).is_furnished || (property as any).isFurnished),
  }, 'fr');

  const propertyData: Omit<FirebaseProperty, 'id'> = {
    ...property,
    public_id: publicId,
    slug: property.slug || smartSlugEn,
    slug_en: smartSlugEn,
    slug_fr: smartSlugFr,
    canonical_url: `https://cameroonlandmarket.com/en/property/${smartSlugEn}`,
    canonical_url_fr: `https://cameroonlandmarket.com/fr/propriete/${smartSlugFr}`,
    previous_slugs: property.previous_slugs || [],
    city_key: cityKey,
    cityKey: cityKey,
    region_key: regionKey,
    regionKey: regionKey,
    title: rawTitle || (detectedLang === 'fr' ? titleFr : titleEn),
    title_en: titleEn,
    title_fr: titleFr,
    description: rawDesc || (detectedLang === 'fr' ? descFr : descEn),
    description_en: descEn,
    description_fr: descFr,
    original_lang: detectedLang,
    original_language: detectedLang,
    originalLanguage: detectedLang,
    translation_status: 'completed',
    translation_source: 'auto',
    translated_at: nowIso,
    localized_title: {
      en: titleEn,
      fr: titleFr,
      originalLanguage: detectedLang,
      translationStatus: 'completed',
    },
    localized_description: {
      en: descEn,
      fr: descFr,
      originalLanguage: detectedLang,
      translationStatus: 'completed',
    },
    published_at: isPublicActive ? (property.published_at || property.publishedAt || nowIso) : undefined,
    publishedAt: isPublicActive ? (property.published_at || property.publishedAt || nowIso) : undefined,
    expires_at: isPublicActive ? (property.expires_at || property.expiresAt || calculatedExpiry) : undefined,
    expiresAt: isPublicActive ? (property.expires_at || property.expiresAt || calculatedExpiry) : undefined,
  };

  // Strict Automated Test Sandbox Isolation:
  // Detects if tests or AI testing agents are running.
  // Intercepts and redirects all creation into the isolated sandbox store,
  // guaranteeing ZERO live listings or test records are pushed to the production database.
  const isTestRunner =
    isTestSandboxSession() ||
    property.owner_email === 'e2e.test.runner@cameroonlandmarket.com' ||
    (typeof property.title === 'string' && /E2E Automated|QA Automation|Test Ad/i.test(property.title));

  if (isTestRunner) {
    console.warn('[CLM TEST SANDBOX] Automated testing runner detected. Storing listing exclusively in isolated sandbox environment — NO live listing pushed to production database.');
    const sandboxResult = saveSandboxProperty({
      ...propertyData,
      id: customId || `clm-sandbox-${publicId.toLowerCase()}`,
      created_at: nowIso,
      updated_at: nowIso,
    }, customId);
    return sandboxResult.id;
  }

  const cleaned = cleanFirestoreData({
    ...propertyData,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });

  try {
    let savedId = customId;
    if (customId) {
      const docRef = doc(db, 'properties', customId);
      await setDoc(docRef, cleaned);
      savedId = customId;
    } else {
      const docRef = await addDoc(collection(db, 'properties'), cleaned);
      savedId = docRef.id;
    }

    // Register slug in slug_registry for atomic uniqueness & tombstone tracking
    try {
      const regDocRef = doc(db, 'slug_registry', slug);
      await setDoc(regDocRef, {
        property_id: savedId,
        public_id: publicId,
        slug,
        created_at: serverTimestamp(),
        active: true,
      }, { merge: true });
    } catch (regErr) {
      console.warn('Notice registering slug in registry:', regErr);
    }

    // Automated Instant Indexing Ping (IndexNow & Google) for both bilingual URLs
    if (property.status !== 'draft') {
      const enUrl = `https://cameroonlandmarket.com/en/property/${smartSlugEn}`;
      const frUrl = `https://cameroonlandmarket.com/fr/propriete/${smartSlugFr}`;
      webmasterClient.dispatchIndexNow([enUrl, frUrl]).catch(() => {});
      webmasterClient.requestGoogleIndexing([enUrl, frUrl]).catch(() => {});
    }

    return savedId!;
  } catch (error) {
    console.error('Error creating property in Firestore:', error);
    handleFirestoreError(error, OperationType.CREATE, 'properties');
  }
}

/**
 * Update an existing property in Cloud Firestore.
 * Keeps URLs stable when editing titles/prices/descriptions.
 * If property type, listing type, or city changes, preserves old slug in previous_slugs for 301 redirects.
 */
export async function updateProperty(propertyId: string, updates: Partial<FirebaseProperty>): Promise<void> {
  // If property type, listing type, city, title status, or furnished status changed, handle redirect history
  if (
    updates.property_type ||
    updates.listing_type ||
    updates.city ||
    (updates as any).has_title !== undefined ||
    (updates as any).has_titre_foncier !== undefined ||
    (updates as any).is_furnished !== undefined ||
    (updates as any).furnished !== undefined
  ) {
    try {
      const currentDoc = await getPropertyById(propertyId);
      if (currentDoc) {
        const oldSlug = currentDoc.slug;
        const oldSlugEn = currentDoc.slug_en || (oldSlug ? getLocalizedPropertySlug(oldSlug, 'en') : null);
        const oldSlugFr = currentDoc.slug_fr || (oldSlug ? getLocalizedPropertySlug(oldSlug, 'fr') : null);
        const publicId = currentDoc.public_id || generatePublicId();
        const newPropertyType = updates.property_type || currentDoc.property_type;
        const newListingType = updates.listing_type || currentDoc.listing_type;
        const newCity = updates.city || currentDoc.city;
        const curAny = currentDoc as any;
        const updAny = updates as any;
        const newHasTitle = updAny.has_title !== undefined
          ? Boolean(updAny.has_title)
          : updAny.has_titre_foncier !== undefined
          ? Boolean(updAny.has_titre_foncier)
          : Boolean(curAny.has_title || curAny.has_titre_foncier || curAny.titre_foncier_number);
        const newIsFurnished = updAny.is_furnished !== undefined
          ? Boolean(updAny.is_furnished)
          : updAny.furnished !== undefined
          ? Boolean(updAny.furnished)
          : Boolean(curAny.is_furnished || curAny.furnished);

        const smartSlugEn = generateSmartPropertySlug({
          propertyType: newPropertyType,
          listingType: newListingType,
          city: newCity,
          publicId,
          hasTitle: newHasTitle,
          isFurnished: newIsFurnished,
        }, 'en');

        const smartSlugFr = generateSmartPropertySlug({
          propertyType: newPropertyType,
          listingType: newListingType,
          city: newCity,
          publicId,
          hasTitle: newHasTitle,
          isFurnished: newIsFurnished,
        }, 'fr');

        if (oldSlug && (smartSlugEn !== oldSlug || smartSlugEn !== oldSlugEn || smartSlugFr !== oldSlugFr)) {
          const prevSlugs = currentDoc.previous_slugs ? [...currentDoc.previous_slugs] : [];
          if (oldSlug && !prevSlugs.includes(oldSlug)) prevSlugs.push(oldSlug);
          if (oldSlugEn && !prevSlugs.includes(oldSlugEn)) prevSlugs.push(oldSlugEn);
          if (oldSlugFr && !prevSlugs.includes(oldSlugFr)) prevSlugs.push(oldSlugFr);

          updates.slug = smartSlugEn;
          updates.slug_en = smartSlugEn;
          updates.slug_fr = smartSlugFr;
          updates.public_id = publicId;
          updates.canonical_url = `https://cameroonlandmarket.com/en/property/${smartSlugEn}`;
          updates.canonical_url_fr = `https://cameroonlandmarket.com/fr/propriete/${smartSlugFr}`;
          updates.previous_slugs = prevSlugs;

          // Update slug registry with both new slugs
          try {
            await Promise.all([
              setDoc(doc(db, 'slug_registry', smartSlugEn), {
                property_id: propertyId,
                public_id: publicId,
                slug: smartSlugEn,
                language: 'en',
                created_at: serverTimestamp(),
                active: true,
              }, { merge: true }),
              setDoc(doc(db, 'slug_registry', smartSlugFr), {
                property_id: propertyId,
                public_id: publicId,
                slug: smartSlugFr,
                language: 'fr',
                created_at: serverTimestamp(),
                active: true,
              }, { merge: true }),
            ]);
          } catch {}
        }
      }
    } catch (e) {
      console.warn('Notice computing updated smart slug:', e);
    }
  }

  // If title or description is being updated, automatically refresh bilingual translations
  if (
    updates.title !== undefined ||
    updates.description !== undefined ||
    updates.title_en !== undefined ||
    updates.title_fr !== undefined ||
    updates.description_en !== undefined ||
    updates.description_fr !== undefined
  ) {
    const rawTitle = (updates.title || updates.title_en || updates.title_fr || '').trim();
    const rawDesc = (updates.description || updates.description_en || updates.description_fr || '').trim();
    if (rawTitle || rawDesc) {
      const detected = detectLanguage(`${rawTitle} ${rawDesc}`);
      try {
        const trans = await translateListingContent({
          title: rawTitle,
          description: rawDesc,
          originalLanguage: detected,
        });
        if (!updates.title_en) updates.title_en = trans.localizedTitle.en;
        if (!updates.title_fr) updates.title_fr = trans.localizedTitle.fr;
        if (!updates.description_en) updates.description_en = trans.localizedDescription.en;
        if (!updates.description_fr) updates.description_fr = trans.localizedDescription.fr;
        updates.original_lang = detected;
        updates.original_language = detected;
        updates.originalLanguage = detected;
        updates.translation_status = 'completed';
        updates.translation_source = 'auto';
        updates.translated_at = new Date().toISOString();
      } catch {}
    }
  }

  const cleaned = cleanFirestoreData({
    ...updates,
    updated_at: serverTimestamp(),
  });

  try {
    const docRef = doc(db, 'properties', propertyId);
    await updateDoc(docRef, cleaned);
  } catch (error) {
    console.warn(`Error updating property ${propertyId} in Firestore, trying setDoc merge fallback:`, error);
    // Attempt fallback with setDoc merge if document didn't exist yet
    try {
      const docRef = doc(db, 'properties', propertyId);
      await setDoc(docRef, cleaned, { merge: true });
    } catch (innerErr) {
      handleFirestoreError(error, OperationType.UPDATE, `properties/${propertyId}`);
    }
  }

  // Automated Search Engine Ping on Updates
  try {
    const docData = await getPropertyById(propertyId);
    const enSlug = updates.slug_en || docData?.slug_en || updates.slug || docData?.slug || propertyId;
    const frSlug = updates.slug_fr || docData?.slug_fr || (enSlug ? getLocalizedPropertySlug(enSlug, 'fr') : propertyId);
    const enUrl = `https://cameroonlandmarket.com/en/property/${enSlug}`;
    const frUrl = `https://cameroonlandmarket.com/fr/propriete/${frSlug}`;
    webmasterClient.dispatchIndexNow([enUrl, frUrl]).catch(() => {});
    webmasterClient.requestGoogleIndexing([enUrl, frUrl]).catch(() => {});
  } catch {}
}

/**
 * Delete a property from Cloud Firestore.
 * Tombstones its slug and public ID so they can never be reused.
 */
export async function deleteProperty(propertyId: string): Promise<void> {
  try {
    // Read property info before deletion to tombstone its slug and publicId
    try {
      const propSnap = await getPropertyById(propertyId);
      if (propSnap && propSnap.slug) {
        const tombstoneRef = doc(db, 'tombstones', propSnap.slug);
        await setDoc(tombstoneRef, {
          property_id: propertyId,
          public_id: propSnap.public_id || null,
          slug: propSnap.slug,
          deleted_at: serverTimestamp(),
          reason: 'owner_deleted',
        });

        // Mark slug_registry as inactive/tombstoned
        await setDoc(doc(db, 'slug_registry', propSnap.slug), {
          active: false,
          deleted_at: serverTimestamp(),
        }, { merge: true });
      }
    } catch (tombErr) {
      console.warn('Notice writing property tombstone:', tombErr);
    }

    const docRef = doc(db, 'properties', propertyId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error(`Error deleting property ${propertyId} from Firestore:`, error);
    handleFirestoreError(error, OperationType.DELETE, `properties/${propertyId}`);
  }
}

/**
 * Submit an inquiry for a property
 */
export async function submitInquiry(inquiry: Omit<FirebaseInquiry, 'id'>): Promise<string> {
  const currentUid = auth.currentUser?.uid;
  const rateKey = `inquiry_${inquiry.sender_phone || currentUid || 'anon'}`;
  const rateCheck = checkRateLimit(rateKey, 15, 60);
  if (!rateCheck.allowed) {
    throw new Error(`Rate limit exceeded. Please wait ${rateCheck.waitSeconds}s before submitting another inquiry.`);
  }

  const riskEval = evaluateInquiryRisk(inquiry.message);

  const cleaned = cleanFirestoreData({
    ...inquiry,
    property_id: inquiry.property_id || '',
    property_title: inquiry.property_title || 'Real Estate Inquiry',
    property_location: inquiry.property_location || '',
    property_price: inquiry.property_price || '',
    property_type: inquiry.property_type || 'land',
    owner_id: inquiry.owner_id || '',
    owner_email: (inquiry.owner_email || '').trim().toLowerCase(),
    owner_name: inquiry.owner_name || '',
    owner_phone: inquiry.owner_phone || '',
    sender_name: sanitizeSafeString(inquiry.sender_name || 'Prospective Buyer', 100),
    sender_phone: (inquiry.sender_phone || '').trim(),
    sender_email: (inquiry.sender_email || '').trim().toLowerCase(),
    sender_user_id: inquiry.sender_user_id || currentUid || '',
    message: sanitizeSafeString(inquiry.message || 'I am interested in this property.', 3000),
    inquiry_type: inquiry.inquiry_type || 'general',
    status: 'new',
    risk_score: riskEval.riskScore,
    risk_flags: riskEval.flaggedReasons,
    is_flagged_risk: riskEval.isSuspicious,
    warning_notice: riskEval.warningNotice || '',
    created_at: serverTimestamp(),
  });
  const docRef = await addDoc(collection(db, 'inquiries'), cleaned);

  if (riskEval.isSuspicious) {
    import('./securityService').then(({ logSecurityThreat }) => {
      logSecurityThreat('advance_fee_fraud', {
        inquiryId: docRef.id,
        reasons: riskEval.flaggedReasons,
        senderPhone: inquiry.sender_phone,
        propertyId: inquiry.property_id,
      }, 'high');
    }).catch(() => {});
  }

  return docRef.id;
}

/**
 * Permanently purge any demo/sample properties seeded earlier in Cloud Firestore
 */
export async function cleanupDemoPropertiesFromFirestore(): Promise<number> {
  let purgedCount = 0;
  try {
    let snap;
    try {
      snap = await getDocsFromServer(collection(db, 'properties'));
    } catch {
      snap = await getDocs(collection(db, 'properties'));
    }

    for (const d of snap.docs) {
      const data = d.data();
      const checkObj = { id: d.id, ...data };

      if (isDemoProperty(checkObj)) {
        try {
          await deleteDoc(d.ref);
          purgedCount++;
        } catch {
          // ignore single doc delete errors
        }
      }
    }

    // Clean legacy mock records from browser storage
    try {
      const storedDossiers = localStorage.getItem('cameroon_cadastral_dossiers');
      if (storedDossiers && (storedDossiers.includes('CAD-YDE-2026-0489') || storedDossiers.includes('TF-23491'))) {
        localStorage.removeItem('cameroon_cadastral_dossiers');
      }
      const storedVisits = localStorage.getItem('cameroon_user_visits');
      if (storedVisits && (storedVisits.includes('vis-1') || storedVisits.includes('Efoulan'))) {
        localStorage.removeItem('cameroon_user_visits');
      }
      const storedFavs = localStorage.getItem('cameroon_favorites');
      if (storedFavs) {
        try {
          const parsed = JSON.parse(storedFavs);
          if (Array.isArray(parsed)) {
            const cleaned = parsed.filter((id: string) => !id.startsWith('prop-demo-') && !/^prop-[0-9]{1,2}$/.test(id));
            localStorage.setItem('cameroon_favorites', JSON.stringify(cleaned));
          }
        } catch {}
      }
    } catch {
      // ignore localStorage clean errors
    }

    // Purge any orphan demo entries from slug_registry and public_id_registry
    try {
      const slugSnap = await getDocs(collection(db, 'slug_registry'));
      for (const sd of slugSnap.docs) {
        const sdata = sd.data();
        if (isDemoProperty({ id: sdata.property_id, slug: sd.id, ...sdata })) {
          await deleteDoc(sd.ref).catch(() => {});
        }
      }
      const idSnap = await getDocs(collection(db, 'public_id_registry'));
      for (const idDoc of idSnap.docs) {
        const idData = idDoc.data();
        if (isDemoProperty({ id: idData.property_id, public_id: idDoc.id, ...idData })) {
          await deleteDoc(idDoc.ref).catch(() => {});
        }
      }
    } catch {
      // ignore registry purge errors
    }
  } catch (error) {
    console.warn('Note: Cleanup demo properties routine notice:', error);
  }
  return purgedCount;
}

/**
 * Fetch all real inquiries from Cloud Firestore
 */
export async function getAllInquiriesFromFirestore(): Promise<PropertyInquiry[]> {
  try {
    let regularInquiries: PropertyInquiry[] = [];
    try {
      const snap = await getDocs(collection(db, 'inquiries'));
      regularInquiries = snap.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          propertyId: data.property_id || '',
          propertyTitle: data.property_title || 'Real Estate Inquiry',
          propertyLocation: data.property_location,
          propertyPrice: data.property_price,
          propertyType: data.property_type,
          ownerId: data.owner_id,
          ownerName: data.owner_name,
          ownerEmail: data.owner_email,
          ownerPhone: data.owner_phone,
          buyerName: data.sender_name || 'Prospective Buyer',
          buyerPhone: data.sender_phone || '',
          buyerEmail: data.sender_email,
          inquiryType: (data.inquiry_type as any) || 'general',
          message: data.message || '',
          createdAt: data.created_at?.toDate ? data.created_at.toDate().toLocaleDateString() : 'Recently',
          status: (data.status as any) || 'new',
        };
      });
    } catch (e) {
      console.warn('Inquiries query notice:', e);
    }

    // Also retrieve contact_messages so every inquiry sent via the Contact page or modal appears directly in the Admin Inquiries panel
    let contactItems: PropertyInquiry[] = [];
    try {
      const contactSnap = await getDocs(collection(db, 'contact_messages'));
      contactItems = contactSnap.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: `contact-${docSnap.id}`,
          propertyId: 'helpdesk-contact',
          propertyTitle: `[Contact Desk] ${data.subject || 'Support & Assistance'}`,
          propertyLocation: 'Support Desk (support@cameroonlandmarket.com)',
          ownerEmail: 'support@cameroonlandmarket.com',
          ownerName: 'Support Team',
          buyerName: data.name || 'Site Visitor',
          buyerPhone: data.phone || '',
          buyerEmail: data.email || '',
          inquiryType: 'general' as any,
          message: data.message || '',
          createdAt: data.created_at?.toDate ? data.created_at.toDate().toLocaleDateString() : 'Recently',
          status: (data.status as any) || 'new',
        };
      });
    } catch (contactErr) {
      console.warn('Notice fetching contact_messages for admin desk:', contactErr);
    }

    return [...contactItems, ...regularInquiries];
  } catch (error) {
    console.warn('Error fetching inquiries from Firestore:', error);
    return [];
  }
}

/**
 * Map Firestore doc to PropertyInquiry
 */
export function mapFirestoreDocToInquiry(docSnap: any): PropertyInquiry {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    propertyId: data.property_id || '',
    propertyTitle: data.property_title || 'Real Estate Inquiry',
    propertyLocation: data.property_location,
    propertyPrice: data.property_price,
    propertyType: data.property_type,
    ownerId: data.owner_id,
    ownerName: data.owner_name,
    ownerEmail: data.owner_email,
    ownerPhone: data.owner_phone,
    senderUserId: data.sender_user_id,
    buyerName: data.sender_name || 'Prospective Buyer',
    buyerPhone: data.sender_phone || '',
    buyerEmail: data.sender_email,
    inquiryType: (data.inquiry_type as any) || 'general',
    message: data.message || '',
    createdAt: data.created_at?.toDate ? data.created_at.toDate().toLocaleDateString() : 'Recently',
    status: (data.status as any) || 'new',
    riskScore: data.risk_score || (data.is_flagged_risk ? 'high' : 'low'),
    riskFlags: Array.isArray(data.risk_flags) ? data.risk_flags : [],
    isFlaggedRisk: data.is_flagged_risk === true || data.risk_score === 'high',
    warningNotice: data.warning_notice || undefined,
    replies: Array.isArray(data.replies)
      ? data.replies.map((r: any) => ({
          id: r.id || `rep-${Math.random()}`,
          senderId: r.sender_id || r.senderId || '',
          senderName: r.sender_name || r.senderName || 'Utilisateur',
          senderRole: r.sender_role || r.senderRole || 'seller',
          message: r.message || '',
          createdAt: r.created_at ? (typeof r.created_at === 'string' ? r.created_at : (r.created_at.toDate ? r.created_at.toDate().toLocaleString() : 'Récemment')) : 'Récemment',
        }))
      : [],
    lastReplyAt: data.last_reply_at?.toDate ? data.last_reply_at.toDate().toLocaleString() : undefined,
    lastReplyText: data.last_reply_text,
  };
}

/**
 * Dedicated HTTPS endpoint running on Cloud Run to deliver emails directly via Zoho SMTP
 */
const DIRECT_EMAIL_DISPATCH_URL = 'https://sendcontactmessagehttp-p6k4b3a7vq-uc.a.run.app';

export async function dispatchDirectEmailViaHttp(payload: {
  to: string;
  subject: string;
  html: string;
  text?: string;
  email?: string;
}): Promise<boolean> {
  try {
    const res = await fetch(DIRECT_EMAIL_DISPATCH_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      console.log(`[DIRECT EMAIL HTTP SUCCESS] Sent to ${payload.to}`);
      return true;
    } else {
      console.warn('[DIRECT EMAIL HTTP WARN]', res.status, await res.text());
      return false;
    }
  } catch (err) {
    console.debug('[DIRECT EMAIL HTTP FALLBACK]', err);
    return false;
  }
}

/**
 * Send an in-platform reply to an inquiry and optionally dispatch an email notification to the recipient
 */
export async function sendInquiryReply(params: {
  inquiryId: string;
  senderId?: string;
  senderName: string;
  senderRole?: 'seller' | 'buyer' | 'admin';
  message: string;
  recipientEmail?: string;
  recipientName?: string;
  propertyTitle?: string;
}): Promise<void> {
  const {
    inquiryId,
    senderId,
    senderName,
    senderRole = 'seller',
    message,
    recipientEmail,
    recipientName = 'Client',
    propertyTitle = 'Bien Immobilier',
  } = params;

  if (!inquiryId || !message.trim()) return;

  const replyObj = {
    id: `reply-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    sender_id: senderId || auth.currentUser?.uid || '',
    sender_name: senderName,
    sender_role: senderRole,
    message: message.trim(),
    created_at: new Date().toISOString(),
  };

  // 1. Update inquiry document in Firestore
  const inquiryRef = doc(db, 'inquiries', inquiryId);
  await updateDoc(inquiryRef, {
    replies: arrayUnion(replyObj),
    status: 'contacted',
    last_reply_at: serverTimestamp(),
    last_reply_text: message.trim(),
    last_reply_sender_name: senderName,
  });

  // 2. Dispatch secure server-side email notification via Cloud Function
  try {
    const notifyReply = httpsCallable(functions, 'sendInquiryReplyNotification');
    await notifyReply({ inquiryId, replyText: message.trim() });
  } catch (mailErr) {
    console.debug('Notice sending inquiry reply notification via Cloud Function:', mailErr);
  }
}

/**
 * Fetch inquiries for properties owned by a specific seller/user
 */
export async function getInquiriesForOwner(ownerId: string, ownerEmail?: string): Promise<PropertyInquiry[]> {
  if (!ownerId && !ownerEmail) return [];
  const map = new Map<string, PropertyInquiry>();
  try {
    if (ownerId) {
      const q = query(collection(db, 'inquiries'), where('owner_id', '==', ownerId));
      const snap = await getDocs(q);
      snap.docs.forEach((d) => map.set(d.id, mapFirestoreDocToInquiry(d)));
    }
    if (ownerEmail) {
      const qEmail = query(collection(db, 'inquiries'), where('owner_email', '==', ownerEmail.trim().toLowerCase()));
      const snapEmail = await getDocs(qEmail);
      snapEmail.docs.forEach((d) => map.set(d.id, mapFirestoreDocToInquiry(d)));
    }
  } catch (error) {
    console.warn('Error fetching inquiries for owner:', error);
  }
  return Array.from(map.values());
}

/**
 * Fetch all inquiries for a user (both as property owner and as buyer)
 */
export async function getInquiriesForUser(userId: string, userEmail?: string): Promise<PropertyInquiry[]> {
  if (!userId && !userEmail) return [];
  const map = new Map<string, PropertyInquiry>();

  try {
    // 1. Inquiries where user is property owner (received)
    if (userId) {
      const qOwner = query(collection(db, 'inquiries'), where('owner_id', '==', userId));
      const snapOwner = await getDocs(qOwner);
      snapOwner.docs.forEach((d) => map.set(d.id, mapFirestoreDocToInquiry(d)));

      // 2. Inquiries where user is sender (sent)
      const qSender = query(collection(db, 'inquiries'), where('sender_user_id', '==', userId));
      const snapSender = await getDocs(qSender);
      snapSender.docs.forEach((d) => map.set(d.id, mapFirestoreDocToInquiry(d)));
    }

    // 3. Match by owner email
    if (userEmail) {
      const cleanEmail = userEmail.trim().toLowerCase();
      const qOwnerEmail = query(collection(db, 'inquiries'), where('owner_email', '==', cleanEmail));
      const snapOwnerEmail = await getDocs(qOwnerEmail);
      snapOwnerEmail.docs.forEach((d) => map.set(d.id, mapFirestoreDocToInquiry(d)));

      // 4. Match by sender email
      const qSenderEmail = query(collection(db, 'inquiries'), where('sender_email', '==', cleanEmail));
      const snapSenderEmail = await getDocs(qSenderEmail);
      snapSenderEmail.docs.forEach((d) => map.set(d.id, mapFirestoreDocToInquiry(d)));
    }
  } catch (error) {
    console.warn('Error fetching inquiries for user:', error);
  }

  return Array.from(map.values());
}

/**
 * Real-time subscription to inquiries for a user (both received as owner and sent as buyer)
 */
export function subscribeToInquiriesForUser(
  userId: string,
  userEmail: string | undefined,
  onUpdate: (inquiries: PropertyInquiry[]) => void
): () => void {
  if (!userId && !userEmail) {
    onUpdate([]);
    return () => {};
  }

  const map = new Map<string, PropertyInquiry>();
  const unsubs: (() => void)[] = [];

  const broadcast = () => {
    onUpdate(Array.from(map.values()));
  };

  const cleanEmail = userEmail ? userEmail.trim().toLowerCase() : '';

  if (userId) {
    // Listen for inquiries where user is the listing owner
    try {
      const qOwner = query(collection(db, 'inquiries'), where('owner_id', '==', userId));
      const unsub1 = onSnapshot(qOwner, (snap) => {
        snap.docChanges().forEach((ch) => {
          if (ch.type === 'removed') map.delete(ch.doc.id);
          else map.set(ch.doc.id, mapFirestoreDocToInquiry(ch.doc));
        });
        broadcast();
      }, (err) => console.warn('Inquiries owner subscription notice:', err));
      unsubs.push(unsub1);
    } catch (e) {
      console.warn('Error setting owner inquiries listener:', e);
    }

    // Listen for inquiries where user is the sender (buyer)
    try {
      const qSender = query(collection(db, 'inquiries'), where('sender_user_id', '==', userId));
      const unsub2 = onSnapshot(qSender, (snap) => {
        snap.docChanges().forEach((ch) => {
          if (ch.type === 'removed') map.delete(ch.doc.id);
          else map.set(ch.doc.id, mapFirestoreDocToInquiry(ch.doc));
        });
        broadcast();
      }, (err) => console.warn('Inquiries sender subscription notice:', err));
      unsubs.push(unsub2);
    } catch (e) {
      console.warn('Error setting sender inquiries listener:', e);
    }
  }

  if (cleanEmail) {
    try {
      const qOwnerEmail = query(collection(db, 'inquiries'), where('owner_email', '==', cleanEmail));
      const unsub3 = onSnapshot(qOwnerEmail, (snap) => {
        snap.docChanges().forEach((ch) => {
          if (ch.type === 'removed') map.delete(ch.doc.id);
          else map.set(ch.doc.id, mapFirestoreDocToInquiry(ch.doc));
        });
        broadcast();
      }, (err) => console.warn('Inquiries owner email subscription notice:', err));
      unsubs.push(unsub3);
    } catch (e) {
      console.warn('Error setting owner email inquiries listener:', e);
    }

    try {
      const qSenderEmail = query(collection(db, 'inquiries'), where('sender_email', '==', cleanEmail));
      const unsub4 = onSnapshot(qSenderEmail, (snap) => {
        snap.docChanges().forEach((ch) => {
          if (ch.type === 'removed') map.delete(ch.doc.id);
          else map.set(ch.doc.id, mapFirestoreDocToInquiry(ch.doc));
        });
        broadcast();
      }, (err) => console.warn('Inquiries sender email subscription notice:', err));
      unsubs.push(unsub4);
    } catch (e) {
      console.warn('Error setting sender email inquiries listener:', e);
    }
  }

  return () => {
    unsubs.forEach((u) => u());
  };
}

/**
 * Real-time subscription to all inquiries (for Admins)
 */
export function subscribeToAllInquiries(
  onUpdate: (inquiries: PropertyInquiry[]) => void
): () => void {
  try {
    const q = collection(db, 'inquiries');
    return onSnapshot(q, (snap) => {
      const list = snap.docs.map(mapFirestoreDocToInquiry);
      onUpdate(list);
    }, (err) => console.warn('All inquiries subscription notice:', err));
  } catch (e) {
    console.warn('Error setting all inquiries listener:', e);
    return () => {};
  }
}

/**
 * Update inquiry status in Cloud Firestore
 */
export async function updateInquiryStatusInFirestore(
  id: string,
  status: 'new' | 'read' | 'contacted'
): Promise<void> {
  try {
    if (id.startsWith('contact-')) {
      const realId = id.replace('contact-', '');
      const docRef = doc(db, 'contact_messages', realId);
      await updateDoc(docRef, {
        status,
        updated_at: serverTimestamp(),
      });
      return;
    }
    const docRef = doc(db, 'inquiries', id);
    await updateDoc(docRef, {
      status,
      updated_at: serverTimestamp(),
    });
  } catch (error) {
    console.warn(`Error updating inquiry ${id} in Firestore:`, error);
  }
}

/**
 * Delete an inquiry from Cloud Firestore
 */
export async function deleteInquiryFromFirestore(id: string): Promise<void> {
  try {
    if (id.startsWith('contact-')) {
      const realId = id.replace('contact-', '');
      const docRef = doc(db, 'contact_messages', realId);
      await deleteDoc(docRef);
      return;
    }
    const docRef = doc(db, 'inquiries', id);
    await deleteDoc(docRef);
  } catch (error) {
    console.warn(`Error deleting inquiry ${id} from Firestore:`, error);
  }
}

/**
 * Upload and record a legal property document (Titre Foncier, Boundary Plan, Non-hypotheque)
 * Stores in /property_documents with security rules restricting access to owner & admins
 */
export async function uploadPropertyDocument(
  propertyId: string,
  ownerId: string,
  docItem: Omit<PropertyDocumentItem, 'id' | 'propertyId' | 'ownerId'>
): Promise<PropertyDocumentItem> {
  const currentUid = auth.currentUser?.uid || ownerId;
  const docData = {
    property_id: propertyId,
    owner_id: currentUid,
    doc_type: docItem.docType,
    title: docItem.title,
    file_url: docItem.fileUrl,
    file_name: docItem.fileName,
    file_size: docItem.fileSize || '2.4 MB',
    sha256_checksum: docItem.sha256_checksum || '',
    uploaded_at: serverTimestamp(),
    verified: Boolean(docItem.verified),
    notes: docItem.notes || '',
  };

  try {
    const docRef = await addDoc(collection(db, 'property_documents'), cleanFirestoreData(docData));
    const created: PropertyDocumentItem = {
      id: docRef.id,
      propertyId,
      ownerId: currentUid,
      docType: docItem.docType,
      title: docItem.title,
      fileUrl: docItem.fileUrl,
      fileName: docItem.fileName,
      fileSize: docItem.fileSize,
      sha256_checksum: docItem.sha256_checksum || '',
      uploadedAt: new Date().toLocaleDateString(),
      verified: Boolean(docItem.verified),
      notes: docItem.notes,
    };

    // Also update public property record with metadata badge only (never leak private download fileUrl)
    try {
      const propRef = doc(db, 'properties', propertyId);
      const propSnap = await getDoc(propRef);
      if (propSnap.exists()) {
        const existingDetails = propSnap.data().verification_details || {};
        const existingDocs = (existingDetails.documents || []).map((d: any) => ({
          id: d.id,
          propertyId: d.propertyId,
          docType: d.docType,
          title: d.title,
          fileName: d.fileName,
          fileSize: d.fileSize,
          sha256_checksum: d.sha256_checksum || '',
          uploadedAt: d.uploadedAt,
          verified: Boolean(d.verified),
        }));

        const publicDocBadge = {
          id: created.id,
          propertyId: created.propertyId,
          docType: created.docType,
          title: created.title,
          fileName: created.fileName,
          fileSize: created.fileSize,
          sha256_checksum: created.sha256_checksum || '',
          uploadedAt: created.uploadedAt,
          verified: Boolean(created.verified),
        };

        await updateDoc(propRef, {
          'verification_details.documents': [...existingDocs, publicDocBadge],
          verification_status: 'documents_uploaded',
          updated_at: serverTimestamp(),
        });
      }
    } catch (innerErr) {
      console.warn('Notice syncing document to property document details:', innerErr);
    }

    return created;
  } catch (error) {
    console.warn('Error recording property document in Firestore:', error);
    // Return local fallback object
    return {
      id: `doc-${Date.now()}`,
      propertyId,
      ownerId: currentUid,
      ...docItem,
      uploadedAt: new Date().toLocaleDateString(),
    };
  }
}

/**
 * Fetch all legal documents uploaded for a specific property
 */
export async function getPropertyDocuments(propertyId: string): Promise<PropertyDocumentItem[]> {
  try {
    const q = query(collection(db, 'property_documents'), where('property_id', '==', propertyId));
    const snap = await getDocs(q);
    return snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        propertyId: data.property_id || propertyId,
        ownerId: data.owner_id,
        docType: data.doc_type || 'titre_foncier',
        title: data.title || 'Cadastral Document',
        fileUrl: data.file_url || '',
        fileName: data.file_name || 'Document.pdf',
        fileSize: data.file_size,
        uploadedAt: data.uploaded_at?.toDate ? data.uploaded_at.toDate().toLocaleDateString() : 'Recently',
        verified: Boolean(data.verified),
        notes: data.notes,
      };
    });
  } catch (error) {
    console.warn('Error fetching property documents from Firestore:', error);
    return [];
  }
}

/**
 * Approve a property's cadastral status and grant the verified badge
 */
export async function approvePropertyVerification(
  propertyId: string,
  verificationData: {
    titreFoncierNumber: string;
    conservationFonciere: string;
    cadastralVolume?: string;
    cadastralFolio?: string;
    parcelNumber?: string;
    surfaceAreaApproved?: string;
    surveyorName?: string;
    surveyorOgecNumber?: string;
    notaryAssigned?: string;
    adminNotes?: string;
  }
): Promise<void> {
  const verifiedTimestamp = new Date().toISOString();
  try {
    // 1. Invoke authoritative admin Cloud Function with verification details
    try {
      await adminSetVerificationStatusBackend(propertyId, 'verified', verificationData.titreFoncierNumber, verificationData);
    } catch (cfErr) {
      console.warn('Cloud Function adminSetVerificationStatus notice (proceeding with Firestore update):', cfErr);
    }

    // 2. Persist full cadastral dossier details to Firestore
    const docRef = doc(db, 'properties', propertyId);
    await updateDoc(docRef, cleanFirestoreData({
      verificationStatus: 'verified',
      verification_status: 'verified',
      verified: true,
      titreFoncier: verificationData.titreFoncierNumber,
      titre_foncier_number: verificationData.titreFoncierNumber,
      'verification_details.titre_foncier_number': verificationData.titreFoncierNumber,
      'verification_details.conservation_fonciere': verificationData.conservationFonciere,
      'verification_details.cadastral_volume': verificationData.cadastralVolume,
      'verification_details.cadastral_folio': verificationData.cadastralFolio,
      'verification_details.parcel_number': verificationData.parcelNumber,
      'verification_details.surface_area_approved': verificationData.surfaceAreaApproved,
      'verification_details.surveyor_name': verificationData.surveyorName,
      'verification_details.surveyor_ogec_number': verificationData.surveyorOgecNumber,
      'verification_details.notary_assigned': verificationData.notaryAssigned,
      'verification_details.verified_at': verifiedTimestamp,
      'verification_details.verified_by': 'SuperAdmin (MINDCAF Audited)',
      'verification_details.admin_notes': verificationData.adminNotes,
      updated_at: serverTimestamp(),
    }));
  } catch (error) {
    console.warn(`Notice approving property ${propertyId} verification:`, error);
  }
}

/**
 * Reject or request amendments for a property's cadastral documents
 */
export async function rejectPropertyVerification(
  propertyId: string,
  reason: string,
  requiresAmendment: boolean = true,
  adminNotes?: string
): Promise<void> {
  const targetStatus: ListingVerificationStatus = requiresAmendment ? 'pending' : 'rejected';
  try {
    // 1. Invoke authoritative admin Cloud Function
    try {
      await adminSetVerificationStatusBackend(propertyId, targetStatus, undefined, {
        reason,
        requiresAmendment,
        adminNotes,
      });
    } catch (cfErr) {
      console.warn('Cloud Function adminSetVerificationStatus notice (proceeding with Firestore update):', cfErr);
    }

    // 2. Persist audit details to Firestore
    const docRef = doc(db, 'properties', propertyId);
    await updateDoc(docRef, cleanFirestoreData({
      verificationStatus: targetStatus,
      verification_status: targetStatus,
      verified: false,
      'verification_details.rejection_reason': reason,
      'verification_details.admin_notes': adminNotes || '',
      updated_at: serverTimestamp(),
    }));
  } catch (error) {
    console.warn(`Notice updating rejection/amendment for property ${propertyId}:`, error);
  }
}

/**
 * Boost a property listing to featured status using MTN Mobile Money or Orange Money,
 * and simultaneously renew/extend the listing's public lifecycle validity.
 */
export type PaymentTransactionStatus = 'pending' | 'completed' | 'failed';

export interface BoostTransactionRecord {
  id: string;
  propertyId: string;
  plan: 'standard' | 'vip' | 'developer';
  durationDays: number;
  amountXaf: number;
  operator: 'mtn_momo' | 'orange_money';
  phone: string;
  userId?: string;
  status: PaymentTransactionStatus;
  failureReason?: string;
  createdAt: string;
  updatedAt?: string;
  expiresAt?: string;
  listingRenewedUntil?: string;
}

/**
 * Initiates a Mobile Money listing boost via Cloud Function callable.
 * Enforces ownership, calculates server pricing, generates UUID, calls gateway,
 * and records the pending transaction server-side.
 */
export async function initiateBoostPaymentOnServer(params: {
  propertyId: string;
  plan: 'standard' | 'vip' | 'developer';
  operator: 'mtn_momo' | 'orange_money';
  phone: string;
}): Promise<{
  success: boolean;
  transactionId: string;
  gatewayReference?: string | null;
  operator: string;
  amount: number;
  durationDays: number;
  ussdCode: string;
  message: string;
}> {
  const callFn = httpsCallable<any, any>(functions, 'initiateBoostPayment');
  const result = await callFn(params);
  return result.data;
}

/**
 * Historical helper: Pending boost transactions are now authoritatively created
 * server-side by initiateBoostPayment.
 */
export async function createPendingBoostTransaction(
  propertyId: string,
  boostData: {
    plan: 'standard' | 'vip' | 'developer';
    durationDays: number;
    operator: 'mtn_momo' | 'orange_money';
    phone: string;
    transactionId: string;
    userId?: string;
  }
): Promise<void> {
  // Server-authoritative: initiateBoostPayment records pending transaction.
}

/**
 * Checks the status of a payment transaction from Firestore (/boost_transactions/{txId}).
 * Supports real-time polling from the front-end modal.
 */
export async function checkPaymentTransactionStatus(
  transactionId: string
): Promise<{
  status: PaymentTransactionStatus;
  transaction?: BoostTransactionRecord;
  error?: string;
}> {
  try {
    const txRef = doc(db, 'boost_transactions', transactionId);
    const snap = await getDoc(txRef);
    if (!snap.exists()) {
      return { status: 'pending' };
    }
    const data = snap.data() as BoostTransactionRecord;
    return {
      status: data.status || 'pending',
      transaction: data,
      error: data.failureReason,
    };
  } catch (err: any) {
    console.warn(`Notice querying payment transaction ${transactionId}:`, err);
    return { status: 'pending' };
  }
}

/**
 * Marks a payment transaction as failed in /boost_transactions/{transactionId}.
 */
export async function markBoostTransactionFailed(
  transactionId: string,
  reason?: string
): Promise<void> {
  console.debug(`[BOOST NOTICE] Transaction ${transactionId} failed or cancelled:`, reason);
}

/**
 * Re-reads property document upon payment completion to retrieve the server-activated state.
 * Client-side direct write has been removed for security; activation occurs server-side via webhook.
 */
export async function boostPropertyWithMobileMoney(
  propertyId: string,
  boostData: {
    plan: 'standard' | 'vip' | 'developer';
    durationDays: number;
    operator: 'mtn_momo' | 'orange_money';
    phone: string;
    transactionId: string;
  }
): Promise<{
  success: boolean;
  propertyId: string;
  featured: boolean;
  status: string;
  expiresAt: string;
  boostExpiresAt: string;
  renewedAt: string;
  transactionId: string;
}> {
  try {
    const docRef = doc(db, 'properties', propertyId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) {
      throw new Error('Property not found');
    }
    const data = snap.data();
    return {
      success: Boolean(data.featured),
      propertyId,
      featured: Boolean(data.featured),
      status: data.status || 'active',
      expiresAt: data.expires_at || data.expiresAt || '',
      boostExpiresAt: data.boost_expires_at || data.boostExpiresAt || '',
      renewedAt: data.renewed_at || data.renewedAt || '',
      transactionId: boostData.transactionId,
    };
  } catch (error) {
    console.warn(`Notice reading boosted property state ${propertyId}:`, error);
    throw error;
  }
}

export interface SuggestedNeighborhood {
  id: string;
  region: string;
  division: string;
  city: string;
  neighborhoodName: string;
  suggestedBy?: string;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  created_at?: any;
  approved_at?: any;
}

export interface ApprovedNeighborhood {
  id: string;
  region: string;
  division: string;
  city: string;
  neighborhoodName: string;
  approvedBy?: string;
  created_at?: any;
}

/**
 * Submits a user-suggested neighborhood for administrative approval.
 * Prevents unauthorized or spam SEO entities while allowing crowdsourced coverage.
 */
export async function submitSuggestedNeighborhood(data: {
  region: string;
  division: string;
  city: string;
  neighborhoodName: string;
  suggestedBy?: string;
}): Promise<void> {
  try {
    await addDoc(collection(db, 'suggested_neighborhoods'), {
      ...data,
      status: 'pending',
      created_at: serverTimestamp(),
    });
  } catch (err) {
    console.warn('Notice submitting suggested neighborhood:', err);
  }
}

/**
 * Fetches suggested neighborhoods for administrative review
 */
export async function getSuggestedNeighborhoods(
  statusFilter: 'pending' | 'approved' | 'rejected' | 'all' = 'all'
): Promise<SuggestedNeighborhood[]> {
  try {
    const colRef = collection(db, 'suggested_neighborhoods');
    let q = query(colRef, orderBy('created_at', 'desc'), limit(100));
    if (statusFilter !== 'all') {
      q = query(colRef, where('status', '==', statusFilter), limit(100));
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        region: data.region || '',
        division: data.division || '',
        city: data.city || '',
        neighborhoodName: data.neighborhoodName || '',
        suggestedBy: data.suggestedBy || 'Anonymous User',
        status: data.status || 'pending',
        rejectionReason: data.rejectionReason,
        created_at: data.created_at?.toDate ? data.created_at.toDate() : data.created_at,
        approved_at: data.approved_at?.toDate ? data.approved_at.toDate() : data.approved_at,
      };
    });
  } catch (err) {
    console.warn('Error fetching suggested neighborhoods:', err);
    return [];
  }
}

/**
 * Approves a suggested neighborhood and registers it into approved_neighborhoods
 */
export async function approveSuggestedNeighborhood(
  id: string,
  details?: {
    region?: string;
    division?: string;
    city?: string;
    neighborhoodName?: string;
    approvedBy?: string;
  }
): Promise<void> {
  try {
    const docRef = doc(db, 'suggested_neighborhoods', id);
    const approvedAt = serverTimestamp();
    await updateDoc(docRef, {
      status: 'approved',
      approved_at: approvedAt,
      ...(details?.neighborhoodName ? { neighborhoodName: details.neighborhoodName } : {}),
      ...(details?.city ? { city: details.city } : {}),
      ...(details?.division ? { division: details.division } : {}),
      ...(details?.region ? { region: details.region } : {}),
    });

    // Also persist to approved_neighborhoods collection for permanent indexing
    if (details?.neighborhoodName && details?.city) {
      await addDoc(collection(db, 'approved_neighborhoods'), {
        neighborhoodName: details.neighborhoodName,
        city: details.city,
        division: details.division || '',
        region: details.region || '',
        approvedBy: details.approvedBy || 'Admin',
        created_at: approvedAt,
      });
    }
  } catch (err) {
    console.warn('Error approving suggested neighborhood:', err);
  }
}

/**
 * Rejects a suggested neighborhood with an optional reason
 */
export async function rejectSuggestedNeighborhood(
  id: string,
  reason: string = 'Non conforme à la nomenclature officielle'
): Promise<void> {
  try {
    const docRef = doc(db, 'suggested_neighborhoods', id);
    await updateDoc(docRef, {
      status: 'rejected',
      rejectionReason: reason,
      rejected_at: serverTimestamp(),
    });
  } catch (err) {
    console.warn('Error rejecting suggested neighborhood:', err);
  }
}

/**
 * Adds an approved neighborhood directly from the admin console
 */
export async function addApprovedNeighborhoodDirect(data: {
  region: string;
  division: string;
  city: string;
  neighborhoodName: string;
  approvedBy?: string;
}): Promise<void> {
  try {
    const ts = serverTimestamp();
    // Add to approved_neighborhoods
    await addDoc(collection(db, 'approved_neighborhoods'), {
      ...data,
      created_at: ts,
    });
    // Also record in suggested_neighborhoods as approved for audit history
    await addDoc(collection(db, 'suggested_neighborhoods'), {
      ...data,
      status: 'approved',
      suggestedBy: data.approvedBy || 'Admin Direct Entry',
      created_at: ts,
      approved_at: ts,
    });
  } catch (err) {
    console.warn('Error directly adding approved neighborhood:', err);
  }
}

/**
 * Retrieves all approved custom neighborhoods from Firestore
 */
export async function getApprovedNeighborhoods(): Promise<ApprovedNeighborhood[]> {
  try {
    const colRef = collection(db, 'approved_neighborhoods');
    const snapshot = await getDocs(query(colRef, limit(300)));
    return snapshot.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        region: data.region || '',
        division: data.division || '',
        city: data.city || '',
        neighborhoodName: data.neighborhoodName || '',
        approvedBy: data.approvedBy,
        created_at: data.created_at,
      };
    });
  } catch (err) {
    console.warn('Error fetching approved neighborhoods:', err);
    return [];
  }
}

/**
 * Approve a pending property listing ad
 */
export async function approveListingAd(propertyId: string): Promise<void> {
  try {
    const docRef = doc(db, 'properties', propertyId);
    await updateDoc(docRef, cleanFirestoreData({
      status: 'published',
      isPublic: true,
      review_status: 'approved',
      reviewed_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    }));

    // Automated Instant Search Engine Indexing on Ad Approval
    try {
      const docData = await getPropertyById(propertyId);
      if (docData) {
        const enSlug = docData.slug_en || docData.slug || propertyId;
        const frSlug = docData.slug_fr || (enSlug ? getLocalizedPropertySlug(enSlug, 'fr') : propertyId);
        const enUrl = `https://cameroonlandmarket.com/en/property/${enSlug}`;
        const frUrl = `https://cameroonlandmarket.com/fr/propriete/${frSlug}`;
        webmasterClient.dispatchIndexNow([enUrl, frUrl]).catch(() => {});
        webmasterClient.requestGoogleIndexing([enUrl, frUrl]).catch(() => {});
      }
    } catch (indexErr) {
      console.warn(`[APPROVE AD INDEXING NOTICE] property ${propertyId}:`, indexErr);
    }
  } catch (err) {
    console.warn(`Error approving ad ${propertyId}:`, err);
  }
}

/**
 * Reject or request amendment for a property listing ad
 */
export async function rejectListingAd(
  propertyId: string,
  reason: string,
  adminNotes?: string
): Promise<void> {
  try {
    const docRef = doc(db, 'properties', propertyId);
    await updateDoc(docRef, cleanFirestoreData({
      status: 'rejected',
      review_status: 'rejected',
      rejection_reason: reason,
      admin_notes: adminNotes || '',
      reviewed_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    }));
  } catch (err) {
    console.warn(`Error rejecting ad ${propertyId}:`, err);
  }
}

/**
 * Unpublish an active listing and revert it to draft
 */
export async function unpublishListingAd(
  propertyId: string,
  reason: string,
  adminEmail?: string
): Promise<void> {
  try {
    const docRef = doc(db, 'properties', propertyId);
    await updateDoc(docRef, cleanFirestoreData({
      status: 'draft',
      isPublic: false,
      unpublish_reason: reason,
      unpublish_by: adminEmail || '',
      unpublished_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    }));
  } catch (err) {
    console.warn(`Error unpublishing ad ${propertyId}:`, err);
  }
}

/**
 * Archive an existing listing
 */
export async function archiveListingAd(
  propertyId: string,
  reason: string,
  adminEmail?: string
): Promise<void> {
  try {
    const docRef = doc(db, 'properties', propertyId);
    await updateDoc(docRef, cleanFirestoreData({
      status: 'archived',
      isPublic: false,
      archive_reason: reason,
      archived_by: adminEmail || '',
      archived_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    }));
  } catch (err) {
    console.warn(`Error archiving ad ${propertyId}:`, err);
  }
}

/**
 * Restore an archived or rejected listing back to pending review
 */
export async function restoreListingAd(
  propertyId: string,
  adminEmail?: string
): Promise<void> {
  try {
    const docRef = doc(db, 'properties', propertyId);
    await updateDoc(docRef, cleanFirestoreData({
      status: 'pending_review',
      isPublic: false,
      restored_by: adminEmail || '',
      restored_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    }));
  } catch (err) {
    console.warn(`Error restoring ad ${propertyId}:`, err);
  }
}

/**
 * Request corrections/amendments on a listing
 */
export async function requestListingCorrections(
  propertyId: string,
  corrections: string,
  adminEmail?: string
): Promise<void> {
  try {
    const docRef = doc(db, 'properties', propertyId);
    await updateDoc(docRef, cleanFirestoreData({
      status: 'pending_review',
      review_status: 'requires_amendment',
      correction_notes: corrections,
      correction_requested_by: adminEmail || '',
      correction_requested_at: serverTimestamp(),
      updated_at: serverTimestamp(),
    }));
  } catch (err) {
    console.warn(`Error requesting corrections for ad ${propertyId}:`, err);
  }
}

/**
 * Update Cadastral Document Review Status with notes and reviewer identity
 */
export async function updateCadastralDocumentReviewStatus(
  propertyId: string,
  cadastralReviewStatus: string,
  notes: string,
  reviewerEmail: string
): Promise<void> {
  try {
    const docRef = doc(db, 'properties', propertyId);
    await updateDoc(docRef, cleanFirestoreData({
      cadastralReviewStatus,
      cadastralReviewNotes: notes,
      cadastralReviewedAt: new Date().toISOString(),
      cadastralReviewerEmail: reviewerEmail,
      updated_at: serverTimestamp(),
    }));
  } catch (err) {
    console.warn(`Error updating cadastral review status for property ${propertyId}:`, err);
  }
}

/**
 * Suspend a user profile with mandatory justification reason
 */
export async function suspendUserInFirestore(
  userId: string,
  reason: string,
  adminEmail?: string
): Promise<void> {
  try {
    const docRef = doc(db, 'users', userId);
    await updateDoc(docRef, cleanFirestoreData({
      isSuspended: true,
      suspensionReason: reason,
      suspendedBy: adminEmail || '',
      suspendedAt: serverTimestamp(),
      updated_at: serverTimestamp(),
    }));
  } catch (err) {
    console.warn(`Error suspending user ${userId}:`, err);
  }
}

/**
 * Reinstate a previously suspended user profile
 */
export async function reinstateUserInFirestore(
  userId: string,
  adminEmail?: string
): Promise<void> {
  try {
    const docRef = doc(db, 'users', userId);
    await updateDoc(docRef, cleanFirestoreData({
      isSuspended: false,
      suspensionReason: null,
      reinstatedBy: adminEmail || '',
      reinstatedAt: serverTimestamp(),
      updated_at: serverTimestamp(),
    }));
  } catch (err) {
    console.warn(`Error reinstating user ${userId}:`, err);
  }
}

/**
 * Increment the real-time view counter for a property listing ad in Firestore.
 */
export async function incrementPropertyViews(propertyId: string): Promise<void> {
  if (!propertyId) return;
  try {
    const docRef = doc(db, 'properties', propertyId);
    await updateDoc(docRef, {
      view_count: increment(1),
    });
  } catch (err) {
    // Non-blocking view tracking
    console.debug(`Notice incrementing view count for property ${propertyId}:`, err);
  }
}

export interface LeadConversionEvent {
  property_id: string;
  lead_type: 'whatsapp' | 'call';
  source?: string;
  created_at?: any;
  user_id?: string | null;
}

/**
 * Track a prospective buyer lead conversion on a property listing (WhatsApp or Direct Call).
 * Atomically increments channel clicks and total leads_generated counter in Firestore,
 * and records an individual event in property_leads collection.
 */
export async function trackPropertyLeadConversion(
  propertyId: string,
  leadType: 'whatsapp' | 'call',
  source = 'listing_detail'
): Promise<void> {
  if (!propertyId) return;

  // 1. Atomically increment counters on the property document
  try {
    const docRef = doc(db, 'properties', propertyId);
    const updatePayload: Record<string, any> = {
      leads_generated: increment(1),
    };
    if (leadType === 'whatsapp') {
      updatePayload.whatsapp_clicks = increment(1);
    } else if (leadType === 'call') {
      updatePayload.call_clicks = increment(1);
    }
    await updateDoc(docRef, updatePayload);
  } catch (err) {
    console.debug(`Notice incrementing ${leadType} lead count for property ${propertyId}:`, err);
  }

  // 2. Also log individual event in property_leads for auditing & analytics
  try {
    const leadsCollection = collection(db, 'property_leads');
    await addDoc(leadsCollection, cleanFirestoreData({
      property_id: propertyId,
      lead_type: leadType,
      source,
      user_id: auth.currentUser?.uid || null,
      created_at: serverTimestamp(),
    }));
  } catch (err) {
    // Non-blocking event logging
    console.debug(`Notice logging lead event for property ${propertyId}:`, err);
  }
}

/**
 * Dispatches an automated buyer lead email notification to the property seller
 * by creating a record in the Firestore 'mail' collection (processed by Firebase Trigger Email).
 */
export async function queueSellerLeadEmailNotification(params: {
  propertyId?: string;
  sellerEmail?: string;
  sellerName?: string;
  buyerName: string;
  buyerPhone?: string;
  buyerEmail?: string;
  propertyTitle?: string;
  propertyLocation?: string;
  propertyPrice?: string | number;
  inquiryType: 'whatsapp' | 'call' | 'visit' | 'general' | 'price' | 'titre_foncier' | 'offer' | 'price_offer';
  message?: string;
  propertyUrl?: string;
  inquiryId?: string;
}): Promise<void> {
  const {
    propertyId,
    buyerName,
    buyerPhone,
    buyerEmail,
    inquiryType,
    message,
    inquiryId,
  } = params;

  if (!propertyId) {
    console.debug('Notice: propertyId missing for queueSellerLeadEmailNotification');
    return;
  }

  try {
    const callSendLead = httpsCallable(functions, 'sendLeadNotification');
    await callSendLead({
      propertyId,
      inquiryType,
      buyerName,
      buyerPhone,
      buyerEmail,
      message,
      inquiryId,
    });
  } catch (err) {
    console.debug('Notice dispatching seller lead notification callable:', err);
  }
}

export interface ContactMessageData {
  name: string;
  phone: string;
  email?: string;
  subject: string;
  message: string;
  source?: string;
  website_url_hp?: string;
}

/**
 * Persist direct contact/helpdesk message to Firestore contact_messages collection
 */
export async function submitContactMessage(data: ContactMessageData): Promise<string> {
  const isTest = isTestSandboxSession();
  if (isTest) {
    console.warn('[CLM TEST SANDBOX] Contact message intercepted in sandbox mode.');
    return `sandbox-contact-${Date.now()}`;
  }

  // Anti-Bot Honeypot Defense: Silent drop if bot field is populated
  if (data.website_url_hp && data.website_url_hp.trim().length > 0) {
    import('./securityService').then(({ logSecurityThreat }) => {
      logSecurityThreat('bot_honeypot', {
        form: 'contact_form',
        trappedInput: data.website_url_hp,
        senderName: data.name,
      }, 'medium').catch(() => {});
    });
    // Silently return fake ID so bot assumes success and does not attempt evasion
    return `bot-trapped-${Date.now()}`;
  }

  const currentUid = auth.currentUser?.uid || '';
  const rateKey = `contact_${data.phone || currentUid || 'anon'}`;
  const rateCheck = checkRateLimit(rateKey, 5, 60);
  if (!rateCheck.allowed) {
    throw new Error(`Trop de messages envoyés. Veuillez patienter ${rateCheck.waitSeconds}s avant de réessayer.`);
  }

  const senderEmail = (data.email || '').trim();
  const safeName = sanitizeSafeString(data.name, 100);
  const safePhone = sanitizeSafeString(data.phone, 30);
  const safeSubject = sanitizeSafeString(data.subject, 150) || 'General Inquiry';
  const safeMessage = sanitizeSafeString(data.message, 3000);

  const payload = cleanFirestoreData({
    name: safeName,
    phone: safePhone,
    email: senderEmail,
    destination_email: 'support@cameroonlandmarket.com',
    subject: safeSubject,
    message: safeMessage,
    source: data.source || 'contact_page',
    user_id: currentUid,
    status: 'new',
    created_at: serverTimestamp(),
  });

  // 1. Always record to contact_messages collection
  const docRef = await addDoc(collection(db, 'contact_messages'), payload);

  // Email notification to support is automatically processed by Cloud Function
  // onContactMessageCreated trigger on contact_messages collection.

  // 3. Direct call to Cloud Function for immediate Zoho SMTP dispatch
  try {
    fetch('https://us-central1-cameroon-land-market-ef9fd.cloudfunctions.net/sendContactMessageHttp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: data.name.trim(),
        phone: data.phone.trim(),
        email: senderEmail,
        subject: data.subject || 'General Inquiry',
        message: data.message.trim(),
        source: data.source || 'contact_page',
      }),
    }).catch((httpErr) => console.debug('HTTP contact mail notice:', httpErr));
  } catch {}

  // 4. If user is authenticated, also record to inquiries collection so it surfaces in real-time admin inquiries table
  if (currentUid && auth.currentUser?.emailVerified) {
    try {
      await addDoc(collection(db, 'inquiries'), cleanFirestoreData({
        property_id: 'helpdesk-contact',
        property_title: data.subject || 'General Inquiries & Legal Desk',
        sender_user_id: currentUid,
        sender_name: data.name.trim(),
        sender_phone: data.phone.trim(),
        sender_email: (data.email || '').trim(),
        inquiry_type: 'general',
        message: data.message.trim(),
        status: 'new',
        created_at: serverTimestamp(),
      }));
    } catch (e) {
      console.debug('Secondary inquiries copy notice:', e);
    }
  }

  return docRef.id;
}

/**
 * Register a user or visitor email to the newsletter subscribers collection in Firestore
 */
export async function subscribeNewsletter(email: string, language: string = 'fr'): Promise<string> {
  const cleanEmail = email.trim().toLowerCase();
  if (!cleanEmail || !cleanEmail.includes('@')) {
    throw new Error('Invalid email address');
  }

  const isTest = isTestSandboxSession();
  if (isTest) {
    return `sandbox-sub-${Date.now()}`;
  }

  const docRef = await addDoc(collection(db, 'newsletter_subscribers'), cleanFirestoreData({
    email: cleanEmail,
    language: language || 'fr',
    subscribed_at: serverTimestamp(),
    active: true,
  }));

  try {
    const existing = JSON.parse(localStorage.getItem('cameroon_newsletter_subscribers') || '[]');
    if (!existing.includes(cleanEmail)) {
      existing.push(cleanEmail);
      localStorage.setItem('cameroon_newsletter_subscribers', JSON.stringify(existing));
    }
  } catch {}

  return docRef.id;
}

export type { ListingAlertSubscription } from './listingAlertStorage';
export {
  getSavedListingAlerts,
  deleteSavedListingAlert,
} from './listingAlertStorage';
import type { ListingAlertSubscription } from './listingAlertStorage';
import { getSavedListingAlerts } from './listingAlertStorage';

/**
 * Generates an official WhatsApp Click-to-Chat URL pre-filled with the buyer's alert criteria.
 */
export function buildWhatsAppAlertActivationUrl(params: Partial<ListingAlertSubscription>): string {
  const isFr = (params.language || 'fr') === 'fr';
  const cleanPhone = (params.whatsappPhone || params.phone || '').trim();
  const categoryLabel = params.category && params.category !== 'all' ? params.category : (isFr ? 'Tous types de biens' : 'All property types');
  const locLabel = params.city ? (params.quarter ? `${params.city} (${params.quarter})` : params.city) : (isFr ? 'Tout le Cameroun' : 'Across Cameroon');
  const budgetLabel = params.maxBudget ? `${Number(params.maxBudget).toLocaleString()} FCFA` : (isFr ? 'Indifférent' : 'Any budget');
  const tfLabel = params.titreFoncierOnly ? (isFr ? 'Oui (Exigé)' : 'Yes (Required)') : (isFr ? 'Non exigé' : 'Not required');

  const text = isFr
    ? `👋 *Bonjour Cameroon Land Market !*\n\nJe souhaite activer mes alertes WhatsApp prioritaires pour de nouvelles annonces foncières :\n\n📍 *Localité :* ${locLabel}\n🏷️ *Catégorie :* ${categoryLabel}\n💰 *Budget max :* ${budgetLabel}\n📜 *Titre Foncier vérifié :* ${tfLabel}\n📱 *Mon numéro :* ${cleanPhone || 'Ce numéro'}\n\nMerci de me transmettre les nouvelles offres validées dès leur mise en ligne !`
    : `👋 *Hello Cameroon Land Market !*\n\nI want to activate priority WhatsApp property alerts for new listings:\n\n📍 *Location:* ${locLabel}\n🏷️ *Category:* ${categoryLabel}\n💰 *Max Budget:* ${budgetLabel}\n📜 *Verified Land Title:* ${tfLabel}\n📱 *My Number:* ${cleanPhone || 'This number'}\n\nPlease send me newly verified opportunities as soon as they are published!`;

  // CLM official customer concierge desk (+237 677 23 45 67)
  return `https://wa.me/237677234567?text=${encodeURIComponent(text)}`;
}

/**
 * Register a user to receive WhatsApp and/or email alerts when listings are posted in a specific city/quarter/category
 */
export async function subscribeListingAlert(params: ListingAlertSubscription): Promise<string> {
  const cleanEmail = (params.email || '').trim().toLowerCase();
  const cleanPhone = (params.whatsappPhone || params.phone || '').trim();
  const channel = params.channel || (cleanPhone && !cleanEmail ? 'whatsapp' : cleanEmail && !cleanPhone ? 'email' : 'both');

  if (channel === 'email' && (!cleanEmail || !cleanEmail.includes('@'))) {
    throw new Error('Invalid email address');
  }
  if (channel === 'whatsapp' && !cleanPhone) {
    throw new Error('Invalid WhatsApp phone number');
  }
  if (!cleanEmail && !cleanPhone) {
    throw new Error('Please provide an email address or WhatsApp phone number');
  }

  // Fallback synthetic email if user only supplied WhatsApp phone to strictly comply with Firestore schema & rules
  const effectiveEmail = cleanEmail || `${cleanPhone.replace(/[^0-9]/g, '') || 'client'}@alerts.cameroonlandmarket.com`;

  const isTest = isTestSandboxSession();
  if (isTest) {
    const dummyId = `sandbox-alert-${Date.now()}`;
    try {
      const existing = getSavedListingAlerts();
      existing.unshift({ ...params, id: dummyId, email: effectiveEmail, created_at: new Date().toISOString() });
      localStorage.setItem('clm_saved_listing_alerts', JSON.stringify(existing.slice(0, 10)));
    } catch {}
    return dummyId;
  }

  const alertPayload = {
    email: effectiveEmail,
    raw_email: cleanEmail || '',
    whatsapp_phone: cleanPhone,
    phone: cleanPhone,
    channel: channel,
    category: params.category || 'all',
    city: params.city || '',
    citySlug: params.citySlug || '',
    quarter: params.quarter || '',
    quarterSlug: params.quarterSlug || '',
    region: params.region || '',
    max_budget: params.maxBudget ? Number(params.maxBudget) : null,
    titre_foncier_only: params.titreFoncierOnly ?? true,
    language: params.language || 'fr',
    source: params.source || 'web_modal',
    created_at: serverTimestamp(),
    active: true,
  };

  const docRef = await addDoc(collection(db, 'listing_alerts'), cleanFirestoreData(alertPayload));

  try {
    const existing = getSavedListingAlerts();
    existing.unshift({
      ...params,
      id: docRef.id,
      email: effectiveEmail,
      channel,
      created_at: new Date().toISOString(),
    });
    localStorage.setItem('clm_saved_listing_alerts', JSON.stringify(existing.slice(0, 10)));
  } catch {}

  return docRef.id;
}




export interface ModerationReportInput {
  propertyId: string;
  propertyTitle?: string;
  propertyLocation?: string;
  reason: string;
  details?: string;
  reporterName?: string;
  reporterContact?: string;
}

/**
 * Persist an ad moderation report directly into Cloud Firestore moderation_reports collection
 * Strict input constraints enforced as per firestore.rules (keys <= 15, propertyId string, reason string)
 */
export async function submitModerationReport(input: ModerationReportInput): Promise<string> {
  const currentUid = auth?.currentUser?.uid || '';
  const safePropId = sanitizeSafeString(input.propertyId, 100);
  const safeReason = sanitizeSafeString(input.reason, 250);

  if (!safePropId) throw new Error('Property ID is required.');
  if (!safeReason || safeReason.length < 3) throw new Error('Reason must be at least 3 characters.');

  const reportPayload = {
    propertyId: safePropId,
    propertyTitle: sanitizeSafeString(input.propertyTitle || 'Property Listing', 100),
    propertyLocation: sanitizeSafeString(input.propertyLocation || '', 100),
    targetType: 'property',
    targetId: safePropId,
    targetTitle: sanitizeSafeString(input.propertyTitle || 'Property Listing', 100),
    reason: safeReason,
    details: sanitizeSafeString(input.details || safeReason, 1000),
    reporterName: sanitizeSafeString(input.reporterName || 'Buyer Community', 100),
    reporterContact: sanitizeSafeString(input.reporterContact || '', 100),
    reporterUid: currentUid || null,
    status: 'new',
    created_at: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, 'moderation_reports'), cleanFirestoreData(reportPayload));
  return docRef.id;
}

/**
 * Fetch all moderation reports from Cloud Firestore for the Administrator Platform
 */
export async function getModerationReportsFromFirestore(): Promise<ModerationReport[]> {
  try {
    const snap = await getDocs(query(collection(db, 'moderation_reports'), orderBy('created_at', 'desc')));
    return snap.docs.map((docSnap) => {
      const d = docSnap.data();
      return {
        id: docSnap.id,
        targetType: d.targetType || 'property',
        targetId: d.targetId || d.propertyId || '',
        targetTitle: d.targetTitle || d.propertyTitle || 'Property Listing',
        reporterName: d.reporterName || 'Anonymous',
        reporterContact: d.reporterContact || '',
        reason: d.reason || '',
        details: d.details || '',
        status: d.status || 'new',
        createdAt: d.created_at?.toDate ? d.created_at.toDate().toISOString() : (d.createdAt || new Date().toISOString()),
        assignedAdmin: d.assignedAdmin,
        resolvedAt: d.resolvedAt,
        resolutionNotes: d.resolutionNotes,
      } as ModerationReport;
    });
  } catch (err) {
    console.warn('Notice fetching moderation reports from Firestore:', err);
    return [];
  }
}

/**
 * Update the status of a moderation report in Cloud Firestore
 */
export async function updateModerationReportStatus(
  reportId: string,
  status: 'new' | 'under_review' | 'resolved' | 'dismissed',
  resolutionNotes?: string
): Promise<void> {
  try {
    const docRef = doc(db, 'moderation_reports', reportId);
    await updateDoc(docRef, cleanFirestoreData({
      status,
      resolutionNotes: resolutionNotes || null,
      resolvedAt: status === 'resolved' || status === 'dismissed' ? serverTimestamp() : null,
      updated_at: serverTimestamp(),
    }));
  } catch (err) {
    console.warn(`Error updating moderation report ${reportId}:`, err);
    throw err;
  }
}
